import { useEffect, useRef, useState, useLayoutEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Application, Graphics, Text } from 'pixi.js';
import { Box } from '@mui/material';
import i18n from '../i18n/config';
import { TOAST_ICONS, formatPoints, formatTeam, SEPARATOR } from '../config/toastIcons';
import Header from './Header';
import GameTable from './GameTable';
import GameStartSequence from './GameStartSequence';
import Deck from '../game/Deck';
import DeckRenderer from '../game/DeckRenderer';
import ScoreMarker from '../game/ScoreMarker';
import ScoreMarkerRenderer from '../game/ScoreMarkerRenderer';
import DealManager from '../game/DealManager';
import ToastManager from '../game/ToastManager';
import DealManagerRenderer from '../game/DealManagerRenderer';
import useLandscapeOrientation from '../hooks/useLandscapeOrientation';
import useIsMobileDevice from '../hooks/useIsMobileDevice';
import useGamePause from '../hooks/useGamePause';
import GlobalMatchScore from '../game/GlobalMatchScore';
import { TEAM_COLORS } from '../theme';
import { GAME_TIMINGS } from '../config/gameTimings';

import GameWinnerModal from './GameWinnerModal';
import RoundWinnerModal from './RoundWinnerModal';
import AIService from '../game/AIService';
import RondaDecisionButtons from './RondaDecisionButtons';
import CaidaOpportunityButtons from './CaidaOpportunityButtons';
import ScoreHistoryModal from './ScoreHistoryModal';
import GameSettingsInGameModal from './GameSettingsInGameModal/GameSettingsInGameModal';
import ShufflingModal from './ShufflingModal';
import RondaHandSummaryModal from './RondaHandSummaryModal';
import statsService from '../services/StatisticsService';
import gameLogger from '../services/GameLogger';
import gameStateManager from '../services/GameStateManager';
import soundVibrationService from '../services/SoundVibrationService';
import { debugLog, debugError, debugWarn, debugTable, debugGroup, debugGroupEnd, isDebugMode, isDebugDeck } from '../utils/debugUtils';

const Ronda = () => {
    const canvasRef = useRef(null);
    const appRef = useRef(null);
    const rendererRef = useRef(null);
    const deckRef = useRef(null);
    const deckRendererRef = useRef(null);
    const dealManagerRef = useRef(null);
    const dealManagerRendererRef = useRef(null);
    const toastManagerRef = useRef(null);

    // Refs for game state logic to avoid stale closures
    const consecutiveDropCountRef = useRef(0);
    const lastDroppedValueRef = useRef(null);
    const startingDealerRef = useRef(null);
    const prevIsPausedRef = useRef(false);

    const location = useLocation();
    const navigate = useNavigate();

    // Get configuration from GameSetup or saved game
    const {
        resumeGame = false,
        savedGameState = null,
        players = [],
        gamePoints = 2,
        gameMode = 'classic',
        globalScore: incomingGlobalScore,
        startingDealer: initialStartingDealer
    } = location.state || {};

    // Debug: Log location.state at component mount
    useEffect(() => {
        debugLog('═══════════════════════════════════════════════════════');
        debugLog('🚀 RONDA COMPONENT MOUNTED');
        debugLog('═══════════════════════════════════════════════════════');
        debugLog('📍 location.state:', location.state);
        debugLog('🔄 resumeGame:', resumeGame);
        debugLog('💾 savedGameState exists:', !!savedGameState);
        if (savedGameState) {
            debugLog('💾 savedGameState preview:', {
                team1Score: savedGameState.team1Score,
                team2Score: savedGameState.team2Score,
                players: savedGameState.players,
                centerCards: savedGameState.centerCards?.length,
            });
        }
        debugLog('👥 players:', players);
        debugLog('═══════════════════════════════════════════════════════');

        // Cleanup when component unmounts
        return () => {
            debugLog('[Ronda] Component unmounting, cleaning up resources');

            // Cancel all active card animations to prevent "Cannot read properties of null" errors
            import('../game/CardAnimations').then(module => {
                module.default.cancelAllAnimations();
            });

            // Cleanup ToastManager
            if (toastManagerRef.current) {
                toastManagerRef.current.destroy();
                toastManagerRef.current = null;
            }

            // Cleanup DealManagerRenderer
            if (dealManagerRendererRef.current) {
                dealManagerRendererRef.current.clearAllCards();
            }

            // Cleanup DeckRenderer
            if (deckRendererRef.current) {
                deckRendererRef.current.destroy();
                deckRendererRef.current = null;
            }
        };
    }, []); // Solo al montar

    // Initialize AIService and difficulty listener
    useEffect(() => {
        aiServiceRef.current = new AIService(null, null);
        aiServiceRef.current.setDifficulty(difficultyRef.current);

        const handleDifficultyChange = (e) => {
            difficultyRef.current = e.detail;
            if (aiServiceRef.current) aiServiceRef.current.setDifficulty(e.detail);
        };
        window.addEventListener('difficultyChanged', handleDifficultyChange);

        return () => {
            window.removeEventListener('difficultyChanged', handleDifficultyChange);
        };
    }, []);

    // Score markers for both teams
    const [team1Marker] = useState(() => new ScoreMarker(1, 'oros', 8));
    const [team2Marker] = useState(() => new ScoreMarker(2, 'oros', 9));
    const [, forceUpdate] = useState({});

    // Global match score (tracks Ronda/Baciga wins)
    const [globalMatchScore] = useState(() => new GlobalMatchScore(incomingGlobalScore));
    const [globalScore, setGlobalScore] = useState(incomingGlobalScore || { team1: 0, team2: 0 });
    const [roundWon, setRoundWon] = useState(false);

    // Winner modal state
    const [showWinnerModal, setShowWinnerModal] = useState(false);
    const [winnerTeamId, setWinnerTeamId] = useState(null);

    // Round Winner Modal state (for round wins, not match wins)
    const [showRoundWinnerModal, setShowRoundWinnerModal] = useState(false);
    const [roundWinnerTeamId, setRoundWinnerTeamId] = useState(null);
    const [pendingNextGame, setPendingNextGame] = useState(null); // Stores transition info for after modal

    // History Modal state
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [selectedTeamId, setSelectedTeamId] = useState(null);
    const [selectedTeamHistory, setSelectedTeamHistory] = useState([]);

    // In-game settings modal state
    const [inGameSettingsOpen, setInGameSettingsOpen] = useState(false);

    // Shuffling modal state (shown when dealer changes, not on first deal)
    const [showShufflingModal, setShowShufflingModal] = useState(false);
    const [shufflingPlayerName, setShufflingPlayerName] = useState('');
    const [shufflingPlayerAvatar, setShufflingPlayerAvatar] = useState('');

    // Hand summary modal state (shown after card counting)
    const [showHandSummaryModal, setShowHandSummaryModal] = useState(false);
    const [handSummaryData, setHandSummaryData] = useState(null);

    // Track current hand number for history
    const [handNumber, setHandNumber] = useState(1);

    // Game state getter for pause hook
    const getGameStateForSave = useCallback(() => {
        // Validar que refs existen
        if (!deckRef.current || !dealManagerRef.current) {
            console.warn('[Ronda] Cannot save - refs not ready');
            return null;
        }

        debugLog('[getGameStateForSave] startingDealer at save time:', startingDealer);

        // Verificar conteo total de cartas
        const totalCards =
            deckRef.current.cards.length +
            dealManagerRef.current.centerCards.length +
            dealManagerRef.current.playerCards[0].length +
            dealManagerRef.current.playerCards[1].length +
            dealManagerRef.current.playerCards[2].length +
            dealManagerRef.current.playerCards[3].length +
            dealManagerRef.current.collectedCards.team1.length +
            dealManagerRef.current.collectedCards.team2.length;

        debugLog('[getGameStateForSave] Card count verification:', {
            deck: deckRef.current.cards.length,
            center: dealManagerRef.current.centerCards.length,
            player0: dealManagerRef.current.playerCards[0].length,
            player1: dealManagerRef.current.playerCards[1].length,
            player2: dealManagerRef.current.playerCards[2].length,
            player3: dealManagerRef.current.playerCards[3].length,
            collected_team1: dealManagerRef.current.collectedCards.team1.length,
            collected_team2: dealManagerRef.current.collectedCards.team2.length,
            TOTAL: totalCards
        });

        if (totalCards !== 40) {
            console.error(`⚠️ [getGameStateForSave] Card count mismatch! Expected 40, got ${totalCards}`);
        }

        return {
            // Metadatos
            gameType: 'ronda',
            savedAt: new Date().toISOString(),

            // Configuración básica
            players: players.map(p => ({ name: p.name, avatar: p.avatar })),
            gamePoints,
            gameMode,
            startingDealer: startingDealer ? { name: startingDealer.name, avatar: startingDealer.avatar } : null,

            // Scores y estado
            team1Score: team1Marker.score,
            team2Score: team2Marker.score,
            team1Status: team1Marker.isInMalas() ? 'malas' : 'buenas',
            team2Status: team2Marker.isInMalas() ? 'malas' : 'buenas',
            team1History: team1Marker.getHistory(),
            team2History: team2Marker.getHistory(),
            globalScore,

            // Estado del juego
            currentTurn: currentTurnRef.current,
            handNumber,
            roundWon,
            consecutiveDropCount: consecutiveDropCountRef.current,
            lastDroppedValue: lastDroppedValueRef.current,

            // Mazo y cartas (NUEVO)
            deckCards: deckRef.current.cards.map(c => ({ suit: c.suit, value: c.value })),
            centerCards: dealManagerRef.current.centerCards.map(c => ({ suit: c.suit, value: c.value })),
            playerCards: {
                0: dealManagerRef.current.playerCards[0]?.map(c => ({ suit: c.suit, value: c.value })) || [],
                1: dealManagerRef.current.playerCards[1]?.map(c => ({ suit: c.suit, value: c.value })) || [],
                2: dealManagerRef.current.playerCards[2]?.map(c => ({ suit: c.suit, value: c.value })) || [],
                3: dealManagerRef.current.playerCards[3]?.map(c => ({ suit: c.suit, value: c.value })) || []
            },

            // Estados adicionales de Ronda (si existen)
            rondaState: rondaState || {},
            caidaOpportunity: caidaOpportunity || {},

            // Cartas recogidas por cada equipo en la mano actual
            // IMPORTANTE: Usar el conteo real de las cartas físicas, no el estado de React
            collectedCards: {
                team1: dealManagerRef.current.collectedCards.team1.length,
                team2: dealManagerRef.current.collectedCards.team2.length
            },
            collectedCardsList: {
                team1: dealManagerRef.current.collectedCards.team1.map(c => ({ suit: c.suit, value: c.value })),
                team2: dealManagerRef.current.collectedCards.team2.map(c => ({ suit: c.suit, value: c.value }))
            },
            lastCollector: lastCollector,
        };
    }, [
        players, gamePoints, gameMode, startingDealer,
        team1Marker, team2Marker, globalScore,
        handNumber, roundWon,
        rondaState, caidaOpportunity, collectedCards, lastCollector
    ]);

    // Use pause hook
    const { isPaused, isPausedRef, handlePauseToggle, waitForUnpause } = useGamePause('ronda', getGameStateForSave);

    // Auto-open modal when game is paused (e.g., when switching browser tabs)
    useEffect(() => {
        if (isPaused && !inGameSettingsOpen) {
            setInGameSettingsOpen(true);
        }
    }, [isPaused, inGameSettingsOpen]);

    // Re-render cards when game is resumed (fixes mobile lock/unlock issue)
    useEffect(() => {
        // Only track changes to isPaused, not gameReady
        // If game was paused and is now unpaused, re-render cards
        if (prevIsPausedRef.current && !isPaused && dealManagerRendererRef.current && gameReady) {
            debugLog('[Ronda] Game resumed after pause - re-rendering cards and score markers');
            const currentState = getGameStateForSave();

            // Clear existing cards first to avoid duplicates
            dealManagerRendererRef.current.clearAllCards();

            // Re-render cards
            dealManagerRendererRef.current.renderRestoredCards(currentState)
                .then(async () => {
                    debugLog('[Ronda] Cards successfully re-rendered after resume');

                    // Re-render score markers completely to fix positioning
                    if (rendererRef.current) {
                        // Destroy existing sprites
                        if (rendererRef.current.sprites) {
                            Object.values(rendererRef.current.sprites).forEach(sprite => {
                                if (sprite && sprite.parent) {
                                    sprite.parent.removeChild(sprite);
                                }
                                if (sprite && sprite.destroy) {
                                    sprite.destroy();
                                }
                            });
                            rendererRef.current.sprites = null;
                        }
                        // Re-render from scratch
                        await rendererRef.current.render();
                        await rendererRef.current.restoreJokerPositions();
                        debugLog('[Ronda] Score markers re-rendered and restored after resume');
                    }

                    // Recover currentTurn if it's null (game would be stuck otherwise)
                    if (currentTurnRef.current === null && !roundWon) {
                        debugLog('[Ronda] currentTurn is null after resume, recovering...');
                        if (currentState && currentState.currentTurn !== null && currentState.currentTurn !== undefined) {
                            setCurrentTurn(currentState.currentTurn);
                            debugLog(`[Ronda] Recovered currentTurn from saved state: ${currentState.currentTurn}`);
                        } else if (startingDealer) {
                            const dealerIdx = players.findIndex(p => p.name === startingDealer.name);
                            const recoveredTurn = dealerIdx >= 0 ? (dealerIdx + 1) % 4 : 0;
                            setCurrentTurn(recoveredTurn);
                            debugLog(`[Ronda] Recovered currentTurn from startingDealer: ${recoveredTurn}`);
                        } else {
                            setCurrentTurn(0);
                            debugLog('[Ronda] Recovered currentTurn to player 0 (fallback)');
                        }
                    } else if (currentTurnRef.current !== null) {
                        // Cards were re-rendered with new sprites, so interactivity was lost.
                        // Re-trigger the turn useEffect by resetting currentTurn.
                        const savedTurn = currentTurnRef.current;
                        setCurrentTurn(null);
                        setTimeout(() => {
                            setCurrentTurn(savedTurn);
                            debugLog(`[Ronda] Re-triggered turn ${savedTurn} after resume to restore interactivity`);
                        }, 50);
                    }
                })
                .catch((error) => {
                    debugError('[Ronda] Error re-rendering cards after resume:', error);
                });
        }

        // Always update the ref to track the current isPaused state
        prevIsPausedRef.current = isPaused;
    }, [isPaused]); // Only depend on isPaused, not gameReady

    // Register game start
    useEffect(() => {
        statsService.registerGameStart('ronda');
    }, []);

    // Configure ScoreMarker win callbacks
    useEffect(() => {
        const handleWin = async (teamId) => {
            // Update global score
            globalMatchScore.addWin(teamId);
            const newGlobalScore = globalMatchScore.getScores();
            setGlobalScore(newGlobalScore);
            setRoundWon(true);

            // Register stats
            statsService.registerWin('ronda');

            debugLog(`🏆 Team ${teamId} wins the round!`);
            debugLog('Global Score:', newGlobalScore);

            // Stop any ongoing turn processing
            setIsProcessingTurn(true);
            setCurrentTurn(null);

            // Check if game is won (reached gamePoints)
            const teamScore = teamId === 1 ? newGlobalScore.team1 : newGlobalScore.team2;

            if (teamScore >= gamePoints) {
                // Game Over - Show Winner Modal
                debugLog(`🏆🏆 GAME OVER! Team ${teamId} wins the match!`);
                setTimeout(() => {
                    setWinnerTeamId(teamId);
                    setShowWinnerModal(true);
                }, GAME_TIMINGS.navigation.beforeWinnerModal);
            } else {
                // Determine next game based on gameMode
                const nextGameName = gameMode === 'classic' ? 'Báciga' : 'Ronda';
                const nextRoute = gameMode === 'classic' ? '/baciga' : '/ronda';

                // Calculate next dealer (use ref to avoid stale closure)
                let nextDealer;
                if (gameMode === 'classic') {
                    nextDealer = startingDealerRef.current
                        ? players[(players.findIndex(p => p.id === startingDealerRef.current.id) + 1) % 4]
                        : players[0];
                } else {
                    // Ronda only mode
                    if (startingDealerRef.current) {
                        const currentDealerIndex = players.findIndex(p => p.id === startingDealerRef.current.id);
                        const nextDealerIndex = (currentDealerIndex + 1) % 4;
                        nextDealer = players[nextDealerIndex];
                    } else {
                        nextDealer = players[0];
                    }
                }

                // Store transition info and show confirmation modal
                setPendingNextGame({
                    nextRoute,
                    nextGameName,
                    winningTeamId: teamId,
                    nextDealer,
                    newGlobalScore
                });
                setRoundWinnerTeamId(teamId);
                setShowRoundWinnerModal(true);
            }
        };

        team1Marker.setOnWin(handleWin);
        team2Marker.setOnWin(handleWin);
    }, [team1Marker, team2Marker, globalMatchScore, gamePoints, gameMode, players, navigate]);

    // Handler for when user confirms round win and wants to continue to next game
    const handleRoundWinnerContinue = async () => {
        if (!pendingNextGame) return;

        const { nextRoute, nextDealer, newGlobalScore, winningTeamId } = pendingNextGame;

        debugLog('🎮 [Ronda] User confirmed round win, proceeding with cleanup and transition');

        // Close the modal first
        setShowRoundWinnerModal(false);

        // Animate all cards returning to deck
        if (dealManagerRendererRef.current && deckRendererRef.current) {
            const deckPosition = deckRendererRef.current.getDeckPosition();
            await dealManagerRendererRef.current.animateCardsReturnToDeck(deckPosition);
        }

        // Reset and shuffle deck
        if (deckRef.current) {
            deckRef.current.reset();
            deckRef.current.shuffle();
            debugLog('🎲 Deck reset and shuffled');
        }

        // Reset deal manager
        if (dealManagerRef.current) {
            dealManagerRef.current.reset();
        }

        // Re-render the deck to show full deck again
        if (deckRendererRef.current) {
            await deckRendererRef.current.render();
        }

        // Reset score markers for next game
        team1Marker.reset();
        team2Marker.reset();
        debugLog('🔄 Score markers reset');

        // Navigate or start new round based on game mode
        if (gameMode === 'classic') {
            // Classic mode: navigate to Baciga
            debugLog('➡️ Moving to Baciga...');
            navigate(nextRoute, {
                state: {
                    players,
                    gamePoints,
                    gameMode,
                    globalScore: newGlobalScore,
                    startingDealer: nextDealer
                }
            });
        } else if (gameMode === 'ronda') {
            // Ronda only mode: start new round in same component
            debugLog('➡️ Starting next Ronda round...');
            await startNewRound(nextDealer);
        }

        // Clear pending state
        setPendingNextGame(null);
        setRoundWinnerTeamId(null);
    };

    // Handler for when user confirms hand summary and wants to continue to next hand or round
    const handleHandSummaryContinue = async () => {
        debugLog('🎮 [Ronda] User confirmed hand summary, continuing...');

        // Close the modal
        setShowHandSummaryModal(false);

        // Check if someone won (score >= 18) - if so, onWin callback handles it
        if (team1Marker.score >= 18 || team2Marker.score >= 18) {
            debugLog('🏆 Game won! Not starting new round.');
            return;
        }

        // In classic mode, wait after card counting before starting new round
        if (gameMode === 'classic') {
            await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.navigation.afterCardCounting));
        }

        // Start new round with next dealer
        setIsProcessingTurn(true);
        await startNewRound();
    };

    // Game start sequence state
    const [showGameStart, setShowGameStart] = useState(!resumeGame && !initialStartingDealer);
    const [startingDealer, setStartingDealer] = useState(initialStartingDealer || null);
    const [gameReady, setGameReady] = useState(false);

    // Keep startingDealerRef in sync with startingDealer state
    useEffect(() => {
        startingDealerRef.current = startingDealer;
    }, [startingDealer]);

    // Game restoration state
    const [isRestoringGame, setIsRestoringGame] = useState(false);
    const [gameRestored, setGameRestored] = useState(false);

    // Force landscape orientation - lock orientation when in game component
    const { needsRotation, isLandscape, isLocked } = useLandscapeOrientation((landscape) => {
        // Pause game when rotating to portrait mode (only if lock failed)
        if (!landscape && handlePauseToggle && !isLocked) {
            handlePauseToggle(true);
            gameLogger.log('orientation_change_pause', { landscape });
        }
    }, { lockOrientation: true });

    // Detect mobile device
    const isMobile = useIsMobileDevice();

    // Header measurement to size the GameTable correctly (100dvh - headerHeight)
    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(0);

    useLayoutEffect(() => {
        const measure = () => {
            if (headerRef.current) {
                const h = Math.round(headerRef.current.getBoundingClientRect().height || 0);
                setHeaderHeight(h);
            }
        };

        measure();

        // Observe header size changes
        let ro;
        if (headerRef.current && typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(() => {
                measure();
            });
            ro.observe(headerRef.current);
        }

        // Also update on window resize as a fallback
        const onResize = () => requestAnimationFrame(measure);
        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
            if (ro) ro.disconnect();
        };
    }, []);

    // Get configuration from GameSetup (moved to top)

    // Redirect to setup if no configuration (unless resuming game)
    useEffect(() => {
        if (!resumeGame && players.length === 0) {
            debugLog('[Ronda] No players configured, redirecting to setup');
            navigate('/setup');
        }
    }, [resumeGame, players, navigate]);

    // Check if we should show the game start sequence
    // Only show when both teams have 0 points (start of the game)
    const shouldShowGameStart = team1Marker.score === 0 && team2Marker.score === 0 && showGameStart;

    // History click handler
    const handleHistoryClick = (teamId) => {
        const marker = teamId === 1 ? team1Marker : team2Marker;
        setSelectedTeamId(teamId);
        setSelectedTeamHistory(marker.getHistory());
        setHistoryModalOpen(true);
    };

    // Game logic setup
    const setupGame = (app) => {
        debugLog('═══════════════════════════════════════════════════════');
        debugLog('⚙️ SETUP GAME EJECUTÁNDOSE');
        debugLog('═══════════════════════════════════════════════════════');
        debugLog('📦 app:', !!app);
        debugLog('🔄 resumeGame:', resumeGame);
        debugLog('💾 savedGameState exists:', !!savedGameState);

        appRef.current = app;

        // Initialize Deck
        const deck = new Deck();
        // Check if debug deck mode is enabled
        if (isDebugDeck()) {
            deck.prepareDebugDeckTripleRonda();
            console.warn('⚠️ DEBUG DECK ACTIVATED: Using prepared deck sequence for testing scenarios (Triple Ronda)');
            if (toastManagerRef.current) {
                toastManagerRef.current.showToast(`${TOAST_ICONS.DEBUG} MODO DEBUG ACTIVO${SEPARATOR}Mazo preparado`, {
                    type: 'warning',
                    duration: 5000,
                    position: 'bottom'
                });
            }
        } else {
            deck.shuffle();
        }
        deckRef.current = deck;

        // Reset card tracker for new round
        if (aiServiceRef.current) aiServiceRef.current.resetCardTracker();

        // Initialize ToastManager
        const toastManager = new ToastManager(app);
        toastManagerRef.current = toastManager;

        // Initialize DealManager
        const dealManager = new DealManager(deck, toastManager);
        dealManagerRef.current = dealManager;

        // Calculate responsive card size based on device type
        const screenWidth = app.screen.width;
        const screenHeight = app.screen.height;

        let cardWidth, cardHeight;
        if (isMobile) {
            // Use 6% of screen width for mobile devices (reduced from 10%)
            cardWidth = Math.floor(screenWidth * 0.06);
            cardHeight = Math.floor(cardWidth * 1.5); // Maintain 2:3 aspect ratio
        } else {
            // Default size for desktop
            cardWidth = 100;
            cardHeight = 150;
        }

        debugLog('🎴 Card sizing:', {
            screenWidth,
            screenHeight,
            isMobile,
            cardWidth,
            cardHeight,
            percentage: isMobile ? '6%' : 'fixed 100px'
        });

        // Initialize and render score markers
        const renderer = new ScoreMarkerRenderer(app, team1Marker, team2Marker);
        rendererRef.current = renderer;
        renderer.setOnHistoryClick(handleHistoryClick);
        renderer.render();

        // Apply responsive card size to score markers
        renderer.setCardSize(cardWidth, cardHeight);

        // If we have a starting dealer OR we're resuming a game, create and position the deck renderer
        if (startingDealer || (resumeGame && savedGameState)) {
            debugLog('🎲 Inicializando DeckRenderer...');
            debugLog('   - startingDealer:', startingDealer);
            debugLog('   - resumeGame:', resumeGame);

            // For resumed games, use the starting dealer from saved state or default to player 0
            let dealerPlayerIndex = 0;
            if (startingDealer) {
                dealerPlayerIndex = players.findIndex(p => p.id === startingDealer.id);
            } else if (resumeGame && savedGameState && savedGameState.startingDealer) {
                const savedDealer = savedGameState.startingDealer;
                dealerPlayerIndex = players.findIndex(p => p.name === savedDealer.name);
                debugLog('   - Usando dealer del estado guardado:', savedDealer, 'índice:', dealerPlayerIndex);
            }

            const deckRenderer = new DeckRenderer(app, deck);

            // Apply card size to deck renderer
            deckRenderer.setCardSize(cardWidth, cardHeight);

            // Position deck based on dealer (dealerIndex is 0-3, but we need to map to player position)
            // Player 1 (index 0) = bottom
            // Player 2 (index 1) = right
            // Player 3 (index 2) = top
            // Player 4 (index 3) = left
            deckRenderer.setPositionByPlayer(dealerPlayerIndex, app.screen.width, app.screen.height);
            deckRenderer.render();
            deckRendererRef.current = deckRenderer;

            // Initialize DealManagerRenderer
            const dealManagerRenderer = new DealManagerRenderer(app, deckRenderer, dealManager);

            // Apply same card size to deal manager renderer
            dealManagerRenderer.setCardSize(cardWidth, cardHeight);

            // Set player names to display on the table
            dealManagerRenderer.setPlayerNames(players);

            dealManagerRendererRef.current = dealManagerRenderer;

            debugLog('✅ SETUP GAME COMPLETADO');
            debugLog('   - Dealer index:', dealerPlayerIndex);
            debugLog('═══════════════════════════════════════════════════════');
            setGameReady(true);
        } else {
            console.error('❌ SETUP GAME NO COMPLETADO: No hay startingDealer ni resumeGame');
        }
    };

    // Handler for when GameStartSequence completes
    const handleGameStartComplete = (result) => {
        debugLog('🎮 Game start complete:', result);
        debugLog('🎲 Starting dealer:', result.startingDealer.avatar, result.startingDealer.name);
        setStartingDealer(result.startingDealer);
        setShowGameStart(false);
    };

    // Function to restore game state from saved data
    const restoreGameState = useCallback(async (state) => {
        debugLog('═══════════════════════════════════════════════════════');
        debugLog('🔄 RESTAURANDO PARTIDA GUARDADA');
        debugLog('═══════════════════════════════════════════════════════');
        debugLog('📋 Estado completo a restaurar:', JSON.stringify(state, null, 2));
        debugLog('---');
        debugLog('🎮 Jugadores:', state.players);
        debugLog('🏆 Team 1 Score:', state.team1Score, '| Team 2 Score:', state.team2Score);
        debugLog('🏆 Team 1 Status:', state.team1Status, '| Team 2 Status:', state.team2Status);
        debugLog('📥 Cartas recogidas - Team 1:', state.collectedCards?.team1 || 0, '| Team 2:', state.collectedCards?.team2 || 0);
        debugLog('👤 Último recolector:', state.lastCollector);
        debugLog('🃏 Cartas del centro:', state.centerCards?.length || 0);
        debugLog('🃏 Cartas jugador 0:', state.playerCards?.[0]?.length || 0);
        debugLog('🃏 Cartas jugador 1:', state.playerCards?.[1]?.length || 0);
        debugLog('🃏 Cartas jugador 2:', state.playerCards?.[2]?.length || 0);
        debugLog('🃏 Cartas jugador 3:', state.playerCards?.[3]?.length || 0);
        debugLog('🃏 Cartas en el mazo:', state.deckCards?.length || 0);
        debugLog('🎯 Turno actual:', state.currentTurn);
        debugLog('📊 Mano número:', state.handNumber);
        debugLog('🔄 Consecutive drop count:', state.consecutiveDropCount);
        debugLog('🎴 Last dropped value:', state.lastDroppedValue);
        debugLog('═══════════════════════════════════════════════════════');

        setIsRestoringGame(true);

        try {
            // Import Card class
            const Card = (await import('../game/Card')).default;

            // 1. Restaurar Deck
            if (state.deckCards && deckRef.current) {
                deckRef.current.cards = state.deckCards.map(c => new Card(c.suit, c.value));
                debugLog('[Ronda] Deck restored:', deckRef.current.cards.length, 'cards');
            }

            // 2. Restaurar DealManager
            if (state.playerCards && state.centerCards && dealManagerRef.current) {
                dealManagerRef.current.playerCards = {
                    0: state.playerCards[0].map(c => new Card(c.suit, c.value)),
                    1: state.playerCards[1].map(c => new Card(c.suit, c.value)),
                    2: state.playerCards[2].map(c => new Card(c.suit, c.value)),
                    3: state.playerCards[3].map(c => new Card(c.suit, c.value))
                };
                dealManagerRef.current.centerCards = state.centerCards.map(c => new Card(c.suit, c.value));

                debugLog('[Ronda] DealManager restored');
                debugLog('   - Center cards in DealManager:', dealManagerRef.current.centerCards.map(c => `${c.suit} ${c.value}`));
                debugLog('   - Player 0 cards:', dealManagerRef.current.playerCards[0].map(c => `${c.suit} ${c.value}`));
                debugLog('   - Player 1 cards:', dealManagerRef.current.playerCards[1].map(c => `${c.suit} ${c.value}`));
                debugLog('   - Player 2 cards:', dealManagerRef.current.playerCards[2].map(c => `${c.suit} ${c.value}`));
                debugLog('   - Player 3 cards:', dealManagerRef.current.playerCards[3].map(c => `${c.suit} ${c.value}`));
            }

            // 3. Restaurar ScoreMarkers
            debugLog('📊 Restaurando ScoreMarkers...');
            debugLog('   - Team1Marker existe:', !!team1Marker);
            debugLog('   - Team2Marker existe:', !!team2Marker);
            debugLog('   - Puntos a restaurar Team1:', state.team1Score);
            debugLog('   - Puntos a restaurar Team2:', state.team2Score);

            // IMPORTANTE: Usar setScore() en lugar de asignación directa para activar callbacks
            if (team1Marker) {
                debugLog('   - Estableciendo score Team1 de', team1Marker.score, 'a', state.team1Score || 0);
                team1Marker.setScore(state.team1Score || 0);
                debugLog('   - Team1 score después de setScore:', team1Marker.score);
            }

            if (team2Marker) {
                debugLog('   - Estableciendo score Team2 de', team2Marker.score, 'a', state.team2Score || 0);
                team2Marker.setScore(state.team2Score || 0);
                debugLog('   - Team2 score después de setScore:', team2Marker.score);
            }

            if (state.team1History) {
                debugLog('   - Restaurando historial Team1:', state.team1History.length, 'entradas');
                team1Marker.restoreHistory(state.team1History);
            }

            if (state.team2History) {
                debugLog('   - Restaurando historial Team2:', state.team2History.length, 'entradas');
                team2Marker.restoreHistory(state.team2History);
            }

            // Actualizar los sprites visuales de los marcadores
            debugLog('   - Llamando a rendererRef.current.restoreJokerPositions()...');
            debugLog('   - rendererRef.current existe:', !!rendererRef.current);
            if (rendererRef.current) {
                await rendererRef.current.restoreJokerPositions();
            }
            forceUpdate({});
            debugLog('✅ ScoreMarkers restaurados');

            // 4. Renderizar cartas restauradas PRIMERO (antes de restaurar el turno)
            if (dealManagerRendererRef.current) {
                await dealManagerRendererRef.current.renderRestoredCards(state);
                debugLog('[Ronda] Cards rendered from saved state');
            }

            // 5. Restaurar estados de UI (handNumber, globalScore, etc.)
            setHandNumber(state.handNumber || 1);
            setGlobalScore(state.globalScore || { team1: 0, team2: 0 });
            setRoundWon(state.roundWon || false);
            debugLog('[Ronda] UI states restored');

            // 6. Restaurar refs
            if (state.consecutiveDropCount !== undefined) {
                consecutiveDropCountRef.current = state.consecutiveDropCount;
            }
            if (state.lastDroppedValue !== undefined) {
                lastDroppedValueRef.current = state.lastDroppedValue;
            }

            // 7. Restaurar estados específicos de Ronda
            if (state.rondaState) setRondaState(state.rondaState);
            if (state.caidaOpportunity) setCaidaOpportunity(state.caidaOpportunity);

            // 8. Restaurar cartas recogidas por cada equipo
            if (state.collectedCards) {
                setCollectedCards(state.collectedCards);
                debugLog('📥 Cartas recogidas (conteo) restauradas:', state.collectedCards);
            }

            // 8b. Restaurar las cartas físicas recogidas en DealManager
            if (state.collectedCardsList && dealManagerRef.current) {
                const { default: Card } = await import('../game/Card.js');
                dealManagerRef.current.collectedCards = {
                    team1: state.collectedCardsList.team1.map(c => new Card(c.suit, c.value)),
                    team2: state.collectedCardsList.team2.map(c => new Card(c.suit, c.value))
                };

                // Actualizar también el estado de React para que coincida
                setCollectedCards({
                    team1: dealManagerRef.current.collectedCards.team1.length,
                    team2: dealManagerRef.current.collectedCards.team2.length
                });

                debugLog('📥 Cartas físicas recogidas restauradas:', {
                    team1: dealManagerRef.current.collectedCards.team1.length,
                    team2: dealManagerRef.current.collectedCards.team2.length
                });
            }

            if (state.lastCollector !== undefined && state.lastCollector !== null) {
                setLastCollector(state.lastCollector);
                debugLog('👤 Último recolector restaurado:', state.lastCollector);
            }

            // 9. Restaurar starting dealer
            if (state.startingDealer) {
                // Reconstruir el objeto completo del dealer con id desde el array de players
                const dealer = players.find(p => p.name === state.startingDealer.name);
                if (dealer) {
                    setStartingDealer(dealer);
                    debugLog('[Ronda] Starting dealer restored:', dealer);
                } else {
                    console.error('[Ronda] Could not find dealer in players:', state.startingDealer.name);
                }
            }

            // 10. Inicializar RondaLogic (necesario para procesar jugadas)
            if (!rondaLogicRef.current) {
                rondaLogicRef.current = new (await import('../game/RondaLogic')).default();
                if (aiServiceRef.current) aiServiceRef.current.setRondaLogic(rondaLogicRef.current);
                debugLog('🎯 RondaLogic initialized for restored game');
            }

            // 11. Marcar dealing como completado (importante para que las cartas sean interactivas)
            setIsDealing(false);
            debugLog('🎴 isDealing set to false');

            // 12. Restaurar turno AL FINAL (después de que las cartas estén renderizadas)
            // Esto disparará el useEffect que hace las cartas interactivas
            const restoredTurn = state.currentTurn !== null && state.currentTurn !== undefined
                ? state.currentTurn
                : (state.startingDealer ? (players.findIndex(p => p.name === state.startingDealer.name) + 1) % 4 : 0);
            setCurrentTurn(restoredTurn);
            debugLog(`[Ronda] Current turn restored to player ${restoredTurn}`);

            debugLog('[Ronda] Game state restored successfully');
        } catch (error) {
            console.error('[Ronda] Error restoring game state:', error);
        } finally {
            setIsRestoringGame(false);
        }
    }, [team1Marker, team2Marker, forceUpdate]);

    // Game logic
    const rondaLogicRef = useRef(null);
    const aiServiceRef = useRef(null);
    const difficultyRef = useRef(localStorage.getItem('difficulty') || 'NORMAL');
    const [isProcessingTurn, setIsProcessingTurn] = useState(false);

    // Turn management
    const [currentTurn, setCurrentTurn] = useState(null);
    const currentTurnRef = useRef(null);
    useEffect(() => { currentTurnRef.current = currentTurn; }, [currentTurn]);

    // Track last player who collected cards and card counts per team
    const [lastCollector, setLastCollector] = useState(null);
    const [collectedCards, setCollectedCards] = useState({
        team1: 0, // Players 0 and 2
        team2: 0  // Players 1 and 3
    });

    // Track consecutive drops for Caídas
    const [lastDroppedValue, setLastDroppedValue] = useState(null);
    const [consecutiveDropCount, setConsecutiveDropCount] = useState(0);

    // State for Caída opportunity (when human player can do a contracaída)
    const [caidaOpportunity, setCaidaOpportunity] = useState({
        active: false,
        cardValue: null,
        caidaType: 'caida', // 'caida', 'contra', 'san_vicente'
        matchingCards: [], // Human player's cards that match
        nextTurnIndex: null, // Who should play next after opportunity resolves
        pendingCollection: null, // Info about pending collection if someone did caída
        timeoutId: null, // For auto-pass timeout
    });
    const caidaOpportunityRef = useRef(caidaOpportunity);
    useEffect(() => { caidaOpportunityRef.current = caidaOpportunity; }, [caidaOpportunity]);

    // Ronda/Rondín declaration state
    const [rondaState, setRondaState] = useState({
        phase: 'none', // 'none', 'detection', 'announcement', 'envio', 'resolved'
        declarations: [], // All detected declarations
        validDeclarations: [], // Valid after applying rondin cancellation rules
        needsEnvio: false,
        envioParticipants: [],
        currentEnvioPlayer: null, // Who is currently deciding in the envio
        envioHistory: [], // Track who said what: { playerIndex, action }
        pendingEnvio: null, // { from: playerIndex } - who sent the envio
        resolution: null, // 'accepted', 'declined', 'a_la_mayor'
        decliningTeam: null, // When resolution='declined', this is the team that declined (1 or 2)
        player1Declaration: null, // The declaration for player 1 (human)
        showButtons: false,
        availableDecisions: [],
    });

    // Track if cards are currently being dealt (for header display)
    const [isDealing, setIsDealing] = useState(true);

    // Track if it's the first game start to avoid double dealing on new rounds
    const isFirstGameStart = useRef(true);

    // Start dealing cards after the game start sequence completes (only for new games)
    useEffect(() => {
        if (!resumeGame && isFirstGameStart.current && !showGameStart && startingDealer && gameReady && dealManagerRef.current && dealManagerRendererRef.current) {
            isFirstGameStart.current = false; // Mark as started so it doesn't run again

            const dealerPlayerIndex = players.findIndex(p => p.id === startingDealer.id);

            // Wait 1 second before starting to deal
            const dealTimeout = setTimeout(async () => {
                debugLog('Starting to deal cards (Initial Game)...');

                // Show shuffling modal for first deal
                setShufflingPlayerName(startingDealer.name);
                setShufflingPlayerAvatar(startingDealer.avatar);
                setShowShufflingModal(true);

                // Wait for shuffling modal duration
                await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.dealing.shufflingModal));

                // Hide shuffling modal
                setShowShufflingModal(false);

                // Deal the first hand (logic)
                const result = await dealManagerRef.current.dealHand(dealerPlayerIndex, true);
                debugLog('First hand dealt (logic):', result);

                // Animate the dealing
                await dealManagerRendererRef.current.animateDealFirstHand(dealerPlayerIndex);
                debugLog('First hand dealt (animation complete)');

                // Track initial center cards for HARD mode AI
                if (aiServiceRef.current && dealManagerRef.current) {
                    aiServiceRef.current.cardTracker.setCenterCards(dealManagerRef.current.getCenterCards());
                }

                // Mark dealing as complete
                setIsDealing(false);

                // Initialize RondaLogic
                rondaLogicRef.current = new (await import('../game/RondaLogic')).default();
                if (aiServiceRef.current) aiServiceRef.current.setRondaLogic(rondaLogicRef.current);

                // Detect Rondas and Rondines
                const firstTurnIndex = (dealerPlayerIndex + 1) % 4;
                await handleRondaDetection(firstTurnIndex);

            }, GAME_TIMINGS.dealing.beforeInitialDeal);

            // Cleanup timeout on unmount
            return () => clearTimeout(dealTimeout);
        }
    }, [showGameStart, startingDealer, players, gameReady, resumeGame]);

    // Restore game state after setupGame completes (for resumed games)
    useEffect(() => {
        debugLog('[Ronda] useEffect - Checking restoration conditions:', {
            gameReady,
            resumeGame,
            hasSavedGameState: !!savedGameState,
            gameRestored
        });

        if (gameReady && resumeGame && savedGameState && !gameRestored) {
            debugLog('[Ronda] ✅ All conditions met, restoring saved state...');
            restoreGameState(savedGameState);
            setGameRestored(true);
        } else {
            debugLog('[Ronda] ❌ Restoration skipped. Reasons:');
            if (!gameReady) debugLog('   - gameReady is false');
            if (!resumeGame) debugLog('   - resumeGame is false');
            if (!savedGameState) debugLog('   - savedGameState is null/undefined');
            if (gameRestored) debugLog('   - gameRestored is already true');
        }
    }, [gameReady, resumeGame, savedGameState, gameRestored, restoreGameState]);

    // Function to handle Ronda/Rondín detection after cards are dealt
    const handleRondaDetection = async (manoPlayerIndex) => {
        if (!dealManagerRef.current || !rondaLogicRef.current) return;

        // Reset ronda state at the start to clear any previous declarations
        // This ensures we don't carry over declarations from the previous hand
        setRondaState(prev => ({
            ...prev,
            declarations: [],
            validDeclarations: [],
            revealedMedias: {},
            resolution: null,
            decliningTeam: null,
            envioAccepted: false,
            phase: 'none',
        }));

        const playerCards = {
            0: dealManagerRef.current.getPlayerCards(0),
            1: dealManagerRef.current.getPlayerCards(1),
            2: dealManagerRef.current.getPlayerCards(2),
            3: dealManagerRef.current.getPlayerCards(3),
        };

        // Detect all Rondas and Rondines
        const declarations = rondaLogicRef.current.detectAllRondasAndRondines(playerCards);

        debugLog('🎯 Detected Rondas/Rondines:', declarations);
        debugLog('🎯 Mano player (first to play):', manoPlayerIndex, `(${players[manoPlayerIndex]?.name})`);

        if (declarations.length === 0) {
            // No declarations, proceed to normal turn
            debugLog('No Rondas or Rondines detected, starting normal play');

            // Clear any previous declarations from state
            setRondaState(prev => ({
                ...prev,
                declarations: [],
                validDeclarations: [],
                revealedMedias: {},
                resolution: null,
                envioAccepted: false,
            }));

            startNormalTurn(manoPlayerIndex);
            return;
        }

        // Process declarations (apply rondin cancellation rules)
        const processed = rondaLogicRef.current.processDeclarations(declarations);
        debugLog('📋 Processed declarations:', processed);

        // Get player 0's declaration if any
        const player1Declaration = processed.validDeclarations.find(d => d.playerIndex === 0);

        // PHASE 1: ANNOUNCEMENTS
        // All players with ronda must announce (cantar) in turn order BEFORE any envío decisions
        // Player 0 (human) will be prompted to announce when it's their turn

        if (processed.validDeclarations.length > 0) {
            // Sort declarations by turn order starting from mano
            const sortedDeclarations = [...processed.validDeclarations].sort((a, b) => {
                const distA = (a.playerIndex - manoPlayerIndex + 4) % 4;
                const distB = (b.playerIndex - manoPlayerIndex + 4) % 4;
                return distA - distB;
            });

            // Check if player 0 has a declaration
            const player0HasDeclaration = sortedDeclarations.some(d => d.playerIndex === 0);

            // If player 0 has a declaration, we need to wait for them to announce
            // before we can determine if envío is needed
            if (player0HasDeclaration) {
                // Set up state for announcement phase - player 0 needs to decide
                setRondaState({
                    phase: 'announcement',
                    declarations,
                    validDeclarations: processed.validDeclarations,
                    needsEnvio: processed.needsEnvio, // May change based on player 0's decision
                    envioParticipants: processed.envioParticipants,
                    currentEnvioPlayer: null,
                    envioHistory: [],
                    pendingEnvio: null,
                    resolution: null,
                    player1Declaration,
                    showButtons: false, // Will be shown when it's player 0's turn
                    availableDecisions: [],
                    manoPlayerIndex,
                    revealedMedias: {},
                    envioAccepted: false,
                    // Track which players have announced
                    announcedPlayers: [],
                    sortedDeclarations,
                });

                // Process announcements in turn order
                await processAnnouncementsInOrder(sortedDeclarations, manoPlayerIndex, processed);
            } else {
                // No player 0 declaration - AI players announce and proceed
                for (const decl of sortedDeclarations) {
                    const typeName = decl.type === 'rondin' ? 'Rondín' : 'Ronda';
                    const playerName = players[decl.playerIndex]?.name || `Jugador ${decl.playerIndex + 1}`;

                    await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.ronda.betweenAnnouncements));

                    if (toastManagerRef.current) {
                        const teamId = decl.playerIndex % 2 === 0 ? 1 : 2;
                        const teamColor = teamId === 1 ? TEAM_COLORS.team1.hex : TEAM_COLORS.team2.hex;
                        toastManagerRef.current.showPlayerToast(`${TOAST_ICONS.DECLARATION} ${typeName}`, decl.playerIndex, {
                            type: 'team' + teamId,
                            teamColor: teamColor,
                            duration: 3000
                        });
                    }
                    // Trigger sound and vibration feedback
                    if (decl.type === 'rondin') {
                        soundVibrationService.triggerRondin();
                    } else {
                        soundVibrationService.triggerRonda();
                    }
                    debugLog(`🎺 ${playerName} (Player ${decl.playerIndex}) announces ${typeName} with value ${decl.value} (${decl.points} points)`);
                }

                // After all AI announcements, check if envío is needed
                if (processed.needsEnvio) {
                    debugLog('⚔️ Envío required between teams!');
                    setRondaState({
                        phase: 'envio',
                        declarations,
                        validDeclarations: processed.validDeclarations,
                        needsEnvio: true,
                        envioParticipants: processed.envioParticipants,
                        currentEnvioPlayer: manoPlayerIndex,
                        envioHistory: [],
                        pendingEnvio: null,
                        resolution: null,
                        player1Declaration: null,
                        showButtons: false,
                        availableDecisions: [],
                        manoPlayerIndex,
                        revealedMedias: {},
                        envioAccepted: false,
                    });
                    // allPlayersAnnounced=true since all AI players have announced (player 0 has no declaration)
                    await processEnvioForAI(manoPlayerIndex, processed.validDeclarations, processed.envioParticipants, null, manoPlayerIndex, true);
                } else {
                    // Only one team has declarations - award points
                    debugLog('✅ Only one team has declarations, awarding points');
                    // Save validDeclarations to state so media announcements work during card play
                    setRondaState(prev => ({
                        ...prev,
                        validDeclarations: processed.validDeclarations,
                        resolution: null,
                        revealedMedias: {},
                        manoPlayerIndex,
                    }));
                    await awardRondaPoints(processed.validDeclarations);
                    startNormalTurn(manoPlayerIndex);
                }
            }
            return; // Exit early - the rest is handled by processAnnouncementsInOrder or above
        }

        // No declarations at all
        debugLog('No Rondas or Rondines detected');
        startNormalTurn(manoPlayerIndex);
        return;

        // Legacy code path - keeping for backwards compatibility but should not be reached
        if (processed.needsEnvio) {
            // Only one team has declarations - award points directly (no need to wait for medias)
            debugLog('✅ Only one team has declarations, awarding points');

            // If player 1 has a declaration, show button to announce or pass
            if (player1Declaration) {
                setRondaState({
                    phase: 'announcement',
                    declarations,
                    validDeclarations: processed.validDeclarations,
                    needsEnvio: false,
                    envioParticipants: [],
                    currentEnvioPlayer: null,
                    envioHistory: [],
                    pendingEnvio: null,
                    resolution: null,
                    player1Declaration,
                    showButtons: true,
                    availableDecisions: ['cantar', 'no_cantar'],
                    manoPlayerIndex,
                    // Reset media tracking for new hand
                    revealedMedias: {},
                    envioAccepted: false,
                });
            } else {
                // Award points to the team with declarations
                // But first, save validDeclarations to state so AI players can announce medias
                setRondaState(prev => ({
                    ...prev,
                    validDeclarations: processed.validDeclarations,
                    resolution: null, // No envío, so medias should be shown
                    revealedMedias: {},
                }));

                await awardRondaPoints(processed.validDeclarations);
                startNormalTurn(manoPlayerIndex);
            }
        } else {
            // All declarations were cancelled
            debugLog('❌ All declarations cancelled by rondines');
            startNormalTurn(manoPlayerIndex);
        }
    };

    // Start normal turn after Ronda/Rondín phase is complete
    const startNormalTurn = (playerIndex) => {
        debugLog(`🎲 startNormalTurn called with playerIndex: ${playerIndex}`);

        // Validate playerIndex
        if (playerIndex === undefined || playerIndex === null) {
            console.error('❌ startNormalTurn called with invalid playerIndex:', playerIndex);
            console.error('   This is likely a bug in the ronda/envío handling code');
            console.error('   Stack trace:', new Error().stack);
            return;
        }

        // Reset ronda state to allow turns to proceed
        // IMPORTANT: We only clear needsEnvio and pendingEnvio to unblock the turn effect.
        // We keep resolution and envioAccepted intact so that checkAndResolveEnvioAfterMedias
        // can still resolve "accepted" or "a_la_mayor" envíos when medias are revealed.
        setRondaState(prev => ({
            ...prev,
            phase: 'none',
            showButtons: false,
            availableDecisions: [],
            // Only clear these to unblock turns - keep resolution/envioAccepted for media resolution
            needsEnvio: false,
            pendingEnvio: null,
        }));

        setCurrentTurn(playerIndex);

        const firstPlayer = players[playerIndex];
        debugLog(`🎲 Turn starts with Player ${playerIndex + 1} (${firstPlayer.name})`);

        if (toastManagerRef.current) {
            const teamId = playerIndex % 2 === 0 ? 1 : 2;
            const teamColor = teamId === 1 ? TEAM_COLORS.team1.hex : TEAM_COLORS.team2.hex;
            toastManagerRef.current.showPlayerToast(`Turno ${TOAST_ICONS.TURN} ${firstPlayer.name}`, playerIndex, {
                type: 'team' + teamId,
                teamColor: teamColor,
                duration: 3000
            });
        }
    };

    // Award points for Ronda/Rondín declarations
    const awardRondaPoints = async (validDeclarations) => {
        let team1Points = 0;
        let team2Points = 0;

        // Group declarations by team for history
        const team1Declarations = validDeclarations.filter(d => d.playerIndex === 0 || d.playerIndex === 2);
        const team2Declarations = validDeclarations.filter(d => d.playerIndex === 1 || d.playerIndex === 3);

        // Process each team's declarations
        [
            { declarations: team1Declarations, teamId: 1, marker: team1Marker },
            { declarations: team2Declarations, teamId: 2, marker: team2Marker }
        ].forEach(({ declarations, teamId, marker }) => {
            if (declarations.length === 0) return;

            const totalTeamPoints = declarations.reduce((sum, d) => sum + d.points, 0);

            // Check if this is a double ronda (both players from the team)
            const hasPlayerDeclaration = declarations.some(d => d.playerIndex === 0);
            const hasPartnerDeclaration = declarations.some(d => d.playerIndex === 2);
            const hasRival1Declaration = declarations.some(d => d.playerIndex === 1);
            const hasRival2Declaration = declarations.some(d => d.playerIndex === 3);

            const isDoubleRonda = (teamId === 1 && hasPlayerDeclaration && hasPartnerDeclaration) ||
                                  (teamId === 2 && hasRival1Declaration && hasRival2Declaration);

            if (isDoubleRonda) {
                // Add a consolidated entry for double ronda
                const type = declarations[0].type === 'rondin' ? 'Rondín Doble' : 'Ronda Doble';
                const actor = teamId === 1 ? 'Jugador y Compañero' : 'Pareja Rival';
                marker.addHistoryEntry(type, actor, totalTeamPoints, handNumber);
            } else {
                // Add individual entries
                declarations.forEach(decl => {
                    const points = decl.points;
                    const type = decl.type === 'rondin' ? 'Rondín' : 'Ronda';

                    // Determine actor string for history
                    let actor = 'Pareja Rival';
                    if (decl.playerIndex === 0) actor = 'Jugador';
                    else if (decl.playerIndex === 2) actor = 'Compañero';

                    marker.addHistoryEntry(type, actor, points, handNumber);
                });
            }

            // Register stats (individual stats still needed)
            declarations.forEach(decl => {
                if (decl.playerIndex === 0) {
                    statsService.registerAction(decl.type === 'rondin' ? 'rondines' : 'rondas', 'player');
                    statsService.registerPoints(decl.points, 'player');
                }
                else if (decl.playerIndex === 2) statsService.registerAction(decl.type === 'rondin' ? 'rondines' : 'rondas', 'partner');
                else statsService.registerAction(decl.type === 'rondin' ? 'rondines' : 'rondas', 'rivals');
            });

            if (teamId === 1) {
                team1Points += totalTeamPoints;
            } else {
                team2Points += totalTeamPoints;
            }
        });

        if (team1Points > 0) {
            debugLog(`🏆 Team 1 gets ${team1Points} points from Ronda/Rondín`);
            await team1Marker.addPoints(team1Points);
            updateScoreMarkers();

            if (toastManagerRef.current) {
                toastManagerRef.current.showToast(`${TOAST_ICONS.TEAM_SCORE} ${formatTeam(1)}${SEPARATOR}${formatPoints(team1Points)}`, {
                    type: 'team1',
                    teamColor: TEAM_COLORS.team1.hex,
                    duration: 2500,
                    position: 'center'
                });
            }
        }

        if (team2Points > 0) {
            debugLog(`🏆 Team 2 gets ${team2Points} points from Ronda/Rondín`);
            await team2Marker.addPoints(team2Points);
            updateScoreMarkers();

            if (toastManagerRef.current) {
                toastManagerRef.current.showToast(`${TOAST_ICONS.TEAM_SCORE} ${formatTeam(2)}${SEPARATOR}${formatPoints(team2Points)}`, {
                    type: 'team2',
                    teamColor: TEAM_COLORS.team2.hex,
                    duration: 2500,
                    position: 'center'
                });
            }
        }

        await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.ronda.afterPointsAwarded));
    };

    /**
     * Handle the human player's decision on a caída opportunity
     * @param {string} decision - 'caer' or 'pasar'
     */
    const handleCaidaDecision = async (decision) => {
        const opportunity = caidaOpportunityRef.current;

        // Clear any auto-pass timeout
        if (opportunity.timeoutId) {
            clearTimeout(opportunity.timeoutId);
        }

        debugLog(`🎯 Human caída decision: ${decision}`);

        // Check if there's a pending collection (someone did caída and we're deciding on contracaída)
        const hasPendingCollection = opportunity.pendingCollection != null;

        if (decision === 'pasar') {
            // Player passed
            if (hasPendingCollection) {
                // Complete the pending collection for the original player
                const pending = opportunity.pendingCollection;

                // Clear opportunity state first
                setCaidaOpportunity({
                    active: false,
                    cardValue: null,
                    caidaType: 'caida',
                    matchingCards: [],
                    nextTurnIndex: null,
                    pendingCollection: null,
                    timeoutId: null,
                });

                // Complete the collection animation for the original player
                await completePendingCollection(pending);

                // Continue with human's turn (they are next)
                setCurrentTurn(0);
                setIsProcessingTurn(false);
            } else {
                // No pending collection - just continue with next turn
                setCaidaOpportunity({
                    active: false,
                    cardValue: null,
                    caidaType: 'caida',
                    matchingCards: [],
                    nextTurnIndex: null,
                    pendingCollection: null,
                    timeoutId: null,
                });

                const nextTurn = opportunity.nextTurnIndex;
                setCurrentTurn(nextTurn);
                setIsProcessingTurn(false);
            }
            return;
        }

        // Player wants to "caer"!
        const matchingCard = opportunity.matchingCards[0];
        const sprites = dealManagerRendererRef.current.getPlayerCardSprites(0);
        const sprite = sprites.find(s =>
            s.cardData.suit === matchingCard.suit && s.cardData.value === matchingCard.value
        );

        if (sprite) {
            // Clear opportunity state before playing
            setCaidaOpportunity({
                active: false,
                cardValue: null,
                caidaType: 'caida',
                matchingCards: [],
                nextTurnIndex: null,
                pendingCollection: null,
                timeoutId: null,
            });

            // Play the card (handleCardPlay will handle the contracaída points)
            // The human's card will collect everything including the pending cards
            await handleCardPlay(0, matchingCard, sprite);
        }
    };

    /**
     * Complete a pending collection after contracaída opportunity was passed
     */
    const completePendingCollection = async (pending) => {
        const { collectorIndex, cardSprite, matchedCards, pilePos, result, currentCaidaCount } = pending;

        // Award caída points - calculate BEFORE animation
        let caidaPoints = 0;
        let caidaType = null;
        if (currentCaidaCount === 2) {
            caidaPoints = 1;
            caidaType = 'Caída';
        } else if (currentCaidaCount === 3) {
            caidaPoints = 2;
            caidaType = 'Contracaída';
        } else if (currentCaidaCount === 4) {
            caidaPoints = 1;
            caidaType = 'San Vicente';
        }

        // Trigger Caída sound and vibration feedback BEFORE the animation
        if (caidaType) {
            soundVibrationService.triggerCaida();
        }

        // The cardSprite is already in the center (from animateCardDrop)
        // We need to animate all cards (cardSprite + matchedCards) to the pile
        const allSprites = [cardSprite, ...matchedCards.map(m => m.sprite)];
        await dealManagerRendererRef.current.animateCardCollection(allSprites, pilePos);

        // Remove cards from center (Logic) - includes the card we added
        const centerCardsBefore = dealManagerRef.current.getCenterCards();
        debugLog(`🧹 [Aseo Debug - Pending] Center cards BEFORE removal: ${centerCardsBefore.length}`,
            centerCardsBefore.map(c => `${c.value}${c.suit}`));
        debugLog(`🧹 [Aseo Debug - Pending] Cards to remove: ${result.centerCardsToRemove.length}`,
            result.centerCardsToRemove.map(c => `${c.value}${c.suit}`));

        result.centerCardsToRemove.forEach(c => dealManagerRef.current.removeCenterCard(c));
        // Also remove the played card that we added to center
        const playedCard = cardSprite.cardData;
        debugLog(`🧹 [Aseo Debug - Pending] Also removing played card: ${playedCard.value}${playedCard.suit}`);
        dealManagerRef.current.removeCenterCard(playedCard);

        const playerTeamId = collectorIndex % 2 === 0 ? 1 : 2;
        const marker = playerTeamId === 1 ? team1Marker : team2Marker;
        const teamColor = playerTeamId === 1 ? TEAM_COLORS.team1.hex : TEAM_COLORS.team2.hex;

        // Check for Aseo
        const centerCardsAfterRemoval = dealManagerRef.current.getCenterCards();
        debugLog(`🧹 [Aseo Debug - Pending] Center cards AFTER removal: ${centerCardsAfterRemoval.length}`,
            centerCardsAfterRemoval.map(c => `${c.value}${c.suit}`));
        const isAseo = centerCardsAfterRemoval.length === 0;

        // Validate if Aseo is valid according to rules
        // Rule: Aseo is ONLY invalid when dealer plays their LAST card in the LAST hand (last card of the game)
        const isLastHand = deckRef.current.remaining === 0;
        const dealerPlayerIndex = startingDealer ? players.findIndex(p => p.id === startingDealer.id) : -1;
        const isDealerLastToPlay = collectorIndex === dealerPlayerIndex;

        // Check if all other players have played their last card (only dealer has cards left or just played)
        const playerCardsAfterPlay = dealManagerRef.current.getPlayerCards(collectorIndex).length;
        const allOtherPlayersEmpty = [0, 1, 2, 3]
            .filter(i => i !== collectorIndex)
            .every(i => dealManagerRef.current.getPlayerCards(i).length === 0);

        // Aseo is invalid ONLY if: last hand + dealer is playing + it's dealer's LAST card + all others already played
        const isAseoValid = !(isLastHand && isDealerLastToPlay && allOtherPlayersEmpty && playerCardsAfterPlay === 0);

        debugLog(`🧹 [Aseo Check - Pending] isAseo=${isAseo}, centerCardsRemaining=${centerCardsAfterRemoval.length}, ` +
            `isLastHand=${isLastHand}, dealerIdx=${dealerPlayerIndex}, collectorIdx=${collectorIndex}, ` +
            `isDealerLastToPlay=${isDealerLastToPlay}, allOthersEmpty=${allOtherPlayersEmpty}, ` +
            `playerCardsAfterPlay=${playerCardsAfterPlay}, isAseoValid=${isAseoValid}`);

        if (isAseo && isAseoValid && caidaType) {
            const totalPoints = 1 + caidaPoints;
            debugLog(`🧹🎯 Aseo + ${caidaType}! Player ${collectorIndex}. +${totalPoints} points for team${playerTeamId}`);
            if (toastManagerRef.current) {
                const caidaIcon = caidaType === 'Caída' ? TOAST_ICONS.CAIDA_3 : caidaType === 'Contracaída' ? TOAST_ICONS.CAIDA_4 : '💫';
                toastManagerRef.current.showPlayerToast(`${TOAST_ICONS.ASEO} Aseo + ${caidaIcon} ${caidaType}`, collectorIndex, {
                    type: 'team' + playerTeamId,
                    teamColor: teamColor,
                    duration: 3000
                });
            }
            // Sound and vibration feedback already triggered before animation
            await marker.addPoints(totalPoints);

            // History & Stats
            let actor = 'Pareja Rival';
            let statActor = 'rivals';
            if (collectorIndex === 0) { actor = 'Jugador'; statActor = 'player'; }
            else if (collectorIndex === 2) { actor = 'Compañero'; statActor = 'partner'; }

            marker.addHistoryEntry('Aseo', actor, 1, handNumber);
            statsService.registerAction('aseos', statActor);
            if (statActor === 'player') statsService.registerPoints(1, 'player');

            marker.addHistoryEntry(caidaType, actor, caidaPoints, handNumber);
            statsService.registerAction(
                caidaType === 'Caída' ? 'caidas' : caidaType === 'Contracaída' ? 'contracaidas' : 'sanVicentes',
                statActor
            );
            if (statActor === 'player') statsService.registerPoints(caidaPoints, 'player');

        } else if (isAseo && isAseoValid) {
            debugLog(`🧹 Aseo! Player ${collectorIndex}. +1 point for team${playerTeamId}`);
            if (toastManagerRef.current) {
                toastManagerRef.current.showPlayerToast(`${TOAST_ICONS.ASEO} Aseo`, collectorIndex, {
                    type: 'team' + playerTeamId,
                    teamColor: teamColor,
                    duration: 2000
                });
            }
            await marker.addPoints(1);

            // History & Stats
            let actor = 'Pareja Rival';
            let statActor = 'rivals';
            if (collectorIndex === 0) { actor = 'Jugador'; statActor = 'player'; }
            else if (collectorIndex === 2) { actor = 'Compañero'; statActor = 'partner'; }

            if (statActor === 'player') statsService.registerPoints(1, 'player');
            marker.addHistoryEntry('Aseo', actor, 1, handNumber);
            statsService.registerAction('aseos', statActor);
        } else if (isAseo && !isAseoValid && caidaType) {
            // Aseo no válido + Caída: solo otorgar puntos de caída
            const totalCaidaPoints = caidaType === 'Caída' ? 1 : caidaType === 'Contracaída' ? 3 : 4;
            debugLog(`🧹🎯 Aseo no válido + ${caidaType}. Player ${collectorIndex}. Solo se otorgan ${totalCaidaPoints} puntos por ${caidaType}.`);
            if (toastManagerRef.current) {
                const caidaIcon = caidaType === 'Caída' ? TOAST_ICONS.CAIDA_3 : caidaType === 'Contracaída' ? TOAST_ICONS.CAIDA_4 : '💫';
                toastManagerRef.current.showPlayerToast(`${caidaIcon} ${caidaType}${SEPARATOR}${TOAST_ICONS.WARNING} Aseo no válido`, collectorIndex, {
                    type: 'team' + playerTeamId,
                    teamColor: teamColor,
                    duration: 3000
                });
            }
            // Sound and vibration feedback already triggered before animation
            await marker.addPoints(caidaPoints);

            // History & Stats - solo caída
            let actor = 'Pareja Rival';
            let statActor = 'rivals';
            if (collectorIndex === 0) { actor = 'Jugador'; statActor = 'player'; }
            else if (collectorIndex === 2) { actor = 'Compañero'; statActor = 'partner'; }

            marker.addHistoryEntry(caidaType, actor, caidaPoints, handNumber);
            statsService.registerAction(
                caidaType === 'Caída' ? 'caidas' : caidaType === 'Contracaída' ? 'contracaidas' : 'sanVicentes',
                statActor
            );
            if (statActor === 'player') statsService.registerPoints(caidaPoints, 'player');
        } else if (isAseo && !isAseoValid) {
            // Aseo no válido (última mano, último jugador/repartidor)
            debugLog(`🧹 Aseo no válido - última mano del repartidor. Player ${collectorIndex}. No se otorgan puntos.`);
            if (toastManagerRef.current) {
                toastManagerRef.current.showPlayerToast(`${TOAST_ICONS.WARNING} Aseo no válido${SEPARATOR}${TOAST_ICONS.LAST_HAND} Última mano`, collectorIndex, {
                    type: 'info',
                    teamColor: teamColor,
                    duration: 2000
                });
            }

        } else if (caidaType) {
            const totalCaidaPoints = caidaType === 'Caída' ? 1 : caidaType === 'Contracaída' ? 3 : 4;
            debugLog(`🎯 ${caidaType}! Player ${collectorIndex}. +${totalCaidaPoints} points for team${playerTeamId}`);
            if (toastManagerRef.current) {
                const caidaIcon = caidaType === 'Caída' ? TOAST_ICONS.CAIDA_3 : caidaType === 'Contracaída' ? TOAST_ICONS.CAIDA_4 : '💫';
                toastManagerRef.current.showPlayerToast(`${caidaIcon} ${caidaType}`, collectorIndex, {
                    type: 'team' + playerTeamId,
                    teamColor: teamColor,
                    duration: 2000
                });
            }
            // Sound and vibration feedback already triggered before animation
            await marker.addPoints(caidaPoints);

            // History & Stats
            let actor = 'Pareja Rival';
            let statActor = 'rivals';
            if (collectorIndex === 0) { actor = 'Jugador'; statActor = 'player'; }
            else if (collectorIndex === 2) { actor = 'Compañero'; statActor = 'partner'; }

            marker.addHistoryEntry(caidaType, actor, caidaPoints, handNumber);
            statsService.registerAction(
                caidaType === 'Caída' ? 'caidas' : caidaType === 'Contracaída' ? 'contracaidas' : 'sanVicentes',
                statActor
            );
            if (statActor === 'player') statsService.registerPoints(caidaPoints, 'player');
        }

        updateScoreMarkers();

        // Reset caída tracking
        setLastDroppedValue(null);
        setConsecutiveDropCount(0);

        // Clean up sprites
        cardSprite.destroy();
        matchedCards.forEach(item => item.sprite.destroy());

        // Track collected cards
        const numCollected = result.collectedCards.length;
        const team = collectorIndex % 2 === 0 ? 'team1' : 'team2';

        // Add collected cards to DealManager
        dealManagerRef.current.addCollectedCards(collectorIndex, result.collectedCards);

        // Track collected cards for HARD mode AI
        if (aiServiceRef.current) {
            const teamId = (collectorIndex === 0 || collectorIndex === 2) ? 1 : 2;
            aiServiceRef.current.cardTracker.recordCollection(result.collectedCards, teamId);
        }

        setCollectedCards(prev => ({
            ...prev,
            [team]: prev[team] + numCollected
        }));
        setLastCollector(collectorIndex);

        // Register card collection stats
        if (collectorIndex === 0) {
            statsService.registerCardCollection(numCollected, true);
        }
        statsService.registerTeamCollection(collectedCards[team] + numCollected);
    };

    // Process announcements (cantar) in turn order
    // This separates the announcement phase from the envío phase
    const processAnnouncementsInOrder = async (sortedDeclarations, manoPlayerIndex, processedData) => {
        debugLog('🎤 Starting announcement phase in turn order');

        // Track which players have announced
        const announcedByTeam = { team1: [], team2: [] };

        for (const decl of sortedDeclarations) {
            const playerTeam = (decl.playerIndex === 0 || decl.playerIndex === 2) ? 'team1' : 'team2';

            if (decl.playerIndex === 0) {
                // Player 0's turn to announce - show buttons and wait
                debugLog(`🎤 Player 0's turn to announce`);

                await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.ronda.betweenAnnouncements));

                setRondaState(prev => ({
                    ...prev,
                    phase: 'announcement',
                    showButtons: true,
                    availableDecisions: ['cantar', 'no_cantar'],
                    currentAnnouncementIndex: sortedDeclarations.indexOf(decl),
                    announcedByTeam,
                    pendingAnnouncementsAfterPlayer0: sortedDeclarations.slice(sortedDeclarations.indexOf(decl) + 1),
                }));

                // Return here - the rest will be handled by handlePlayer1RondaDecision
                return;
            } else {
                // AI player announces automatically
                const typeName = decl.type === 'rondin' ? 'Rondín' : 'Ronda';
                const playerName = players[decl.playerIndex]?.name || `Jugador ${decl.playerIndex + 1}`;

                await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.ronda.betweenAnnouncements));

                if (toastManagerRef.current) {
                    const teamId = decl.playerIndex % 2 === 0 ? 1 : 2;
                    const teamColor = teamId === 1 ? TEAM_COLORS.team1.hex : TEAM_COLORS.team2.hex;
                    toastManagerRef.current.showPlayerToast(`${TOAST_ICONS.DECLARATION} ${typeName}`, decl.playerIndex, {
                        type: 'team' + teamId,
                        teamColor: teamColor,
                        duration: 3000
                    });
                }

                // Trigger sound and vibration feedback
                if (decl.type === 'rondin') {
                    soundVibrationService.triggerRondin();
                } else {
                    soundVibrationService.triggerRonda();
                }

                debugLog(`🎺 ${playerName} (Player ${decl.playerIndex}) announces ${typeName} with value ${decl.value} (${decl.points} points)`);
                announcedByTeam[playerTeam].push(decl.playerIndex);
            }
        }

        // If we get here, all players announced (no player 0 in declarations)
        // This shouldn't happen if player 0 has a declaration, but handle it anyway
        await finishAnnouncementPhase(announcedByTeam, processedData, manoPlayerIndex);
    };

    // Helper to finish announcement phase using current state
    const finishAnnouncementPhaseFromState = async (announcedByTeam) => {
        const manoPlayerIndex = rondaState.manoPlayerIndex;

        debugLog('🎤 Finishing announcement phase from state', announcedByTeam);

        // Get the declarations that were actually announced
        const announcedDeclarations = rondaState.validDeclarations.filter(d => {
            const team = (d.playerIndex === 0 || d.playerIndex === 2) ? 'team1' : 'team2';
            return announcedByTeam[team].includes(d.playerIndex);
        });

        // Check if both teams have announced declarations
        const team1Announced = announcedByTeam.team1.length > 0;
        const team2Announced = announcedByTeam.team2.length > 0;

        if (team1Announced && team2Announced) {
            // Both teams have announcements - envío is needed
            debugLog('⚔️ Both teams announced - starting envío phase!');

            const envioParticipants = announcedDeclarations.map(d => d.playerIndex);

            // Create envioHistory with all players who announced (so they don't get asked to cantar again)
            const envioHistory = envioParticipants.map(p => ({ playerIndex: p, action: 'cantar' }));

            setRondaState(prev => ({
                ...prev,
                phase: 'envio',
                validDeclarations: announcedDeclarations,
                needsEnvio: true,
                envioParticipants,
                showButtons: false,
                envioHistory,
            }));

            await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.ronda.betweenAnnouncements));
            // Pass allPlayersAnnounced=true since announcement phase is complete
            await processEnvioForAI(manoPlayerIndex, announcedDeclarations, envioParticipants, null, manoPlayerIndex, true);
        } else {
            // Only one team (or no team) announced - award points directly
            if (announcedDeclarations.length > 0) {
                debugLog('✅ Only one team announced, awarding points');

                // Create envioHistory with all players who announced (so they show "Media" messages)
                const envioHistory = announcedDeclarations.map(d => ({ playerIndex: d.playerIndex, action: 'cantar' }));

                setRondaState(prev => ({
                    ...prev,
                    envioHistory,
                }));

                await awardRondaPoints(announcedDeclarations);
            } else {
                debugLog('❌ No team announced their ronda');
            }
            startNormalTurn(manoPlayerIndex);
        }
    };

    // Called after all announcements are complete to start envío or award points
    const finishAnnouncementPhase = async (announcedByTeam, processedData, manoPlayerIndex) => {
        debugLog('🎤 Announcement phase complete', announcedByTeam);

        // Get the declarations that were actually announced
        const announcedDeclarations = processedData.validDeclarations.filter(d => {
            const team = (d.playerIndex === 0 || d.playerIndex === 2) ? 'team1' : 'team2';
            return announcedByTeam[team].includes(d.playerIndex);
        });

        // Check if both teams have announced declarations
        const team1Announced = announcedByTeam.team1.length > 0;
        const team2Announced = announcedByTeam.team2.length > 0;

        if (team1Announced && team2Announced) {
            // Both teams have announcements - envío is needed
            debugLog('⚔️ Both teams announced - starting envío phase!');

            const envioParticipants = announcedDeclarations.map(d => d.playerIndex);

            // Create envioHistory with all players who announced (so they don't get asked to cantar again)
            const envioHistory = envioParticipants.map(p => ({ playerIndex: p, action: 'cantar' }));

            setRondaState(prev => ({
                ...prev,
                phase: 'envio',
                validDeclarations: announcedDeclarations,
                needsEnvio: true,
                envioParticipants,
                showButtons: false,
                envioHistory,
            }));

            await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.ronda.betweenAnnouncements));
            // Pass allPlayersAnnounced=true since announcement phase is complete
            await processEnvioForAI(manoPlayerIndex, announcedDeclarations, envioParticipants, null, manoPlayerIndex, true);
        } else {
            // Only one team (or no team) announced - award points directly
            if (announcedDeclarations.length > 0) {
                debugLog('✅ Only one team announced, awarding points');

                // Create envioHistory with all players who announced (so they show "Media" messages)
                const envioHistory = announcedDeclarations.map(d => ({ playerIndex: d.playerIndex, action: 'cantar' }));

                setRondaState(prev => ({
                    ...prev,
                    envioHistory,
                }));

                await awardRondaPoints(announcedDeclarations);
            } else {
                debugLog('❌ No team announced their ronda');
            }
            startNormalTurn(manoPlayerIndex);
        }
    };

    // Helper function to check if a player is the last one with ronda in turn order
    const isLastPlayerWithRonda = (playerIndex, envioParticipants, manoPlayerIndex) => {
        // Sort participants by turn order starting from mano
        const sortedParticipants = [...envioParticipants].sort((a, b) => {
            const distA = (a - manoPlayerIndex + 4) % 4;
            const distB = (b - manoPlayerIndex + 4) % 4;
            return distA - distB;
        });
        return sortedParticipants[sortedParticipants.length - 1] === playerIndex;
    };

    // Helper function to get available decisions based on current state
    const getAvailableDecisions = (playerIndex, envioParticipants, hasPendingEnvio, manoPlayerIndex) => {
        const isLast = isLastPlayerWithRonda(playerIndex, envioParticipants, manoPlayerIndex);

        if (hasPendingEnvio) {
            // Someone has already sent an envío - can only accept or reject
            return ['acepto', 'no_acepto'];
        } else {
            // No pending envío
            if (isLast) {
                // Last player can't say chiquita - must decide
                return ['enviar', 'a_la_mayor'];
            } else {
                // Not the last player - can pass with chiquita
                return ['enviar', 'chiquita'];
            }
        }
    };

    // Process envío for AI players
    // allPlayersAnnounced: if true, skip the cantar/no_cantar check for player 0
    const processEnvioForAI = async (currentPlayerIndex, validDeclarations, envioParticipants, pendingEnvioFrom = null, manoPlayerIndex = null, allPlayersAnnounced = false) => {
        // Find the next player with a declaration in turn order
        let playerIndex = currentPlayerIndex;
        // Use passed manoPlayerIndex or fall back to state
        const actualManoPlayerIndex = manoPlayerIndex !== null ? manoPlayerIndex : rondaState.manoPlayerIndex;

        // Use passed parameter or fall back to state
        const hasPendingEnvio = pendingEnvioFrom !== null || rondaState.pendingEnvio !== null;
        const actualPendingEnvio = pendingEnvioFrom !== null ? { from: pendingEnvioFrom } : rondaState.pendingEnvio;

        // If the current player is not in envioParticipants, find the next one in turn order who is
        if (!envioParticipants.includes(playerIndex)) {
            debugLog(`🔍 Player ${playerIndex} not in envioParticipants. Finding next participant in turn order...`);

            // Sort participants by turn order starting from manoPlayerIndex
            const sortedParticipants = [...envioParticipants].sort((a, b) => {
                const distA = (a - actualManoPlayerIndex + 4) % 4;
                const distB = (b - actualManoPlayerIndex + 4) % 4;
                return distA - distB;
            });

            playerIndex = sortedParticipants[0]; // Take the first participant in turn order
            debugLog(`🔍 Next participant: ${playerIndex} (${players[playerIndex]?.name})`);
        }

        if (envioParticipants.includes(playerIndex)) {
            if (playerIndex === 0) {
                // It's player 0's turn to decide
                // If allPlayersAnnounced is true, we know all players (including player 0) have announced
                // This is passed from finishAnnouncementPhaseFromState after the announcement phase completes
                const hasAnnounced = allPlayersAnnounced ||
                                     rondaState.phase === 'envio' ||
                                     rondaState.envioHistory.some(h => h.playerIndex === 0 && h.action === 'cantar');

                let availableDecisions;
                if (!hasAnnounced) {
                    // Player 0 hasn't announced yet, show cantar/no_cantar options first
                    availableDecisions = ['cantar', 'no_cantar'];
                } else {
                    // Player 0 has already announced, show envío decisions
                    availableDecisions = getAvailableDecisions(playerIndex, envioParticipants, hasPendingEnvio, actualManoPlayerIndex);
                }

                debugLog(`🎯 Player 0's turn - allPlayersAnnounced: ${allPlayersAnnounced}, hasAnnounced: ${hasAnnounced}, hasPendingEnvio: ${hasPendingEnvio}, decisions: ${availableDecisions}`);

                setRondaState(prev => ({
                    ...prev,
                    currentEnvioPlayer: playerIndex,
                    showButtons: true,
                    availableDecisions,
                    pendingEnvio: actualPendingEnvio,
                    phase: 'envio',
                }));
                return; // Wait for player 0 to decide
            }

            // AI decides
            await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.envio.aiDecisionDelay));

            const playerName = players[playerIndex]?.name || `Jugador ${playerIndex + 1}`;
            const aiDeclaration = validDeclarations.find(d => d.playerIndex === playerIndex);

            // Get available decisions for AI player
            const availableDecisions = getAvailableDecisions(playerIndex, envioParticipants, hasPendingEnvio, actualManoPlayerIndex);

            // AI decision delegated to AIService
            const envioGameState = {
                hasPendingEnvio,
                availableDecisions,
                manoPlayerIndex: actualManoPlayerIndex,
            };
            const { decision: aiDecision } = aiServiceRef.current.decideEnvio(
                playerIndex, aiDeclaration, envioGameState
            );

            debugLog(`🤖 ${playerName} decides: ${aiDecision}`);

            if (toastManagerRef.current) {
                let message = '';
                const teamId = playerIndex % 2 === 0 ? 1 : 2;
                const teamColor = teamId === 1 ? TEAM_COLORS.team1.hex : TEAM_COLORS.team2.hex;
                switch (aiDecision) {
                    case 'enviar':
                        message = `${TOAST_ICONS.ENVIO} Envío`;
                        break;
                    case 'chiquita':
                        message = `${TOAST_ICONS.CHIQUITA} Chiquita`;
                        break;
                    case 'acepto':
                        message = `${TOAST_ICONS.ACCEPT} Acepto`;
                        break;
                    case 'no_acepto':
                        message = `${TOAST_ICONS.REJECT} No acepto`;
                        break;
                    case 'a_la_mayor':
                        message = `${TOAST_ICONS.A_LA_MAYOR} A la mayor`;
                        break;
                }
                // Show toast near the player who made the decision with team color
                toastManagerRef.current.showPlayerToast(message, playerIndex, {
                    type: 'team' + teamId,
                    teamColor: teamColor,
                    duration: 3500
                });
            }

            // Process the decision
            if (aiDecision === 'enviar') {
                setRondaState(prev => ({
                    ...prev,
                    pendingEnvio: { from: playerIndex },
                    envioHistory: [...prev.envioHistory, { playerIndex, action: 'enviar' }],
                }));

                // After sending, find the next player from the opposing team with ronda
                const senderTeam = (playerIndex === 0 || playerIndex === 2) ? 1 : 2;
                const sortedParticipants = [...envioParticipants].sort((a, b) => {
                    const distA = (a - actualManoPlayerIndex + 4) % 4;
                    const distB = (b - actualManoPlayerIndex + 4) % 4;
                    return distA - distB;
                });

                // Find next opponent with ronda in turn order
                let nextOpponent = null;
                const currentIdx = sortedParticipants.indexOf(playerIndex);
                for (let i = 1; i <= sortedParticipants.length; i++) {
                    const nextIdx = (currentIdx + i) % sortedParticipants.length;
                    const candidatePlayer = sortedParticipants[nextIdx];
                    const candidateTeam = (candidatePlayer === 0 || candidatePlayer === 2) ? 1 : 2;
                    if (candidateTeam !== senderTeam) {
                        nextOpponent = candidatePlayer;
                        break;
                    }
                }

                if (nextOpponent !== null) {
                    await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.envio.transitionDelay));
                    // Pass playerIndex as pendingEnvioFrom so the next player knows there's a pending envío
                    // allPlayersAnnounced=true since we're in envío phase
                    await processEnvioForAI(nextOpponent, validDeclarations, envioParticipants, playerIndex, actualManoPlayerIndex, true);
                }
                return;

            } else if (aiDecision === 'chiquita') {
                setRondaState(prev => ({
                    ...prev,
                    envioHistory: [...prev.envioHistory, { playerIndex, action: 'chiquita' }],
                }));

                // After chiquita, pass to next player with ronda in turn order
                const sortedParticipants = [...envioParticipants].sort((a, b) => {
                    const distA = (a - actualManoPlayerIndex + 4) % 4;
                    const distB = (b - actualManoPlayerIndex + 4) % 4;
                    return distA - distB;
                });
                const currentIdx = sortedParticipants.indexOf(playerIndex);
                const nextParticipant = sortedParticipants[(currentIdx + 1) % sortedParticipants.length];

                if (nextParticipant !== playerIndex) {
                    // Wait longer before showing next player's options so the current decision is visible
                    await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.envio.transitionDelay + 500));
                    // allPlayersAnnounced=true since we're in envío phase
                    await processEnvioForAI(nextParticipant, validDeclarations, envioParticipants, null, actualManoPlayerIndex, true);
                }
                return;

            } else if (aiDecision === 'acepto') {
                // Mark envío as accepted - winner will be determined when medias are revealed
                // IMPORTANT: Always start with manoPlayerIndex, not the player who accepted
                const correctManoPlayer = actualManoPlayerIndex;

                setRondaState(prev => ({
                    ...prev,
                    envioAccepted: true,
                    resolution: 'accepted',
                }));

                if (toastManagerRef.current) {
                    toastManagerRef.current.showToast(`${TOAST_ICONS.ACCEPT} Envío aceptado${SEPARATOR}Se determina al ver las medias`, {
                        type: 'info',
                        duration: 4000,
                        position: 'center'
                    });
                }
                await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.envio.aiDecisionDelay));
                startNormalTurn(correctManoPlayer);
                return;
            } else if (aiDecision === 'no_acepto') {
                // Resolve envío - sender gets only their points
                // Pass the sender (pendingEnvioFrom) directly since state may not be updated yet
                const correctManoPlayer = actualManoPlayerIndex;
                await resolveEnvio('declined', validDeclarations, correctManoPlayer, pendingEnvioFrom);
                return;
            } else if (aiDecision === 'a_la_mayor') {
                // Resolve by highest card - also waits for medias
                // IMPORTANT: Always start with manoPlayerIndex, not the player who chose a_la_mayor
                const correctManoPlayer = actualManoPlayerIndex;

                setRondaState(prev => ({
                    ...prev,
                    envioAccepted: true,
                    resolution: 'a_la_mayor',
                }));

                if (toastManagerRef.current) {
                    toastManagerRef.current.showToast(`${TOAST_ICONS.A_LA_MAYOR} A la mayor${SEPARATOR}Se determina al ver las medias`, {
                        type: 'info',
                        duration: 4000,
                        position: 'center'
                    });
                }
                await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.envio.aiDecisionDelay));
                startNormalTurn(correctManoPlayer);
                return;
            }
        }
    };

    // Resolve envío
    const resolveEnvio = async (resolution, validDeclarations, manoPlayerIndex, senderPlayerIndex = null) => {
        debugLog(`📜 Resolving envío: ${resolution}`);

        const result = rondaLogicRef.current.resolveEnvio(
            validDeclarations,
            resolution,
            null, // winnerPlayerIndex will be calculated
            manoPlayerIndex
        );

        if (resolution === 'declined') {
            // Find who sent the envío and award points only to their team
            // Use passed senderPlayerIndex first, then fall back to state
            const sender = senderPlayerIndex !== null ? senderPlayerIndex : rondaState.pendingEnvio?.from;
            debugLog(`📜 Envío declined - sender: ${sender}, senderPlayerIndex param: ${senderPlayerIndex}, state pendingEnvio: ${JSON.stringify(rondaState.pendingEnvio)}`);
            debugLog(`📜 Valid declarations:`, validDeclarations);
            if (sender !== undefined) {
                const senderTeam = (sender === 0 || sender === 2) ? 1 : 2;
                const declinerTeam = senderTeam === 1 ? 2 : 1;

                // Mark resolution as declined and store which team declined (to hide their media)
                setRondaState(prev => ({
                    ...prev,
                    resolution: 'declined',
                    decliningTeam: declinerTeam, // The team that rejected must NOT show media
                }));
                const senderDeclarations = validDeclarations.filter(d => {
                    const team = (d.playerIndex === 0 || d.playerIndex === 2) ? 1 : 2;
                    return team === senderTeam;
                });
                debugLog(`📜 Sender team ${senderTeam} declarations:`, senderDeclarations);

                const points = senderDeclarations.reduce((sum, d) => sum + d.points, 0);
                debugLog(`📜 Total points to award: ${points}`);

                const marker = senderTeam === 1 ? team1Marker : team2Marker;
                await marker.addPoints(points);
                updateScoreMarkers();

                // Add history entry for envío declined
                const type = 'Envío Rechazado';
                let actor = 'Pareja Rival';
                if (sender === 0) actor = 'Jugador';
                else if (sender === 2) actor = 'Compañero';
                marker.addHistoryEntry(type, actor, points, handNumber);

                const senderTeamColor = senderTeam === 1 ? TEAM_COLORS.team1.hex : TEAM_COLORS.team2.hex;
                if (toastManagerRef.current) {
                    toastManagerRef.current.showToast(`${TOAST_ICONS.REJECT} Envío rechazado\n${formatTeam(senderTeam)}${SEPARATOR}${formatPoints(points)}`, {
                        type: 'team' + senderTeam,
                        teamColor: senderTeamColor,
                        duration: 3000,
                        position: 'center'
                    });
                }
            }
        } else if (result) {
            // Award points to winning team
            const marker = result.winningTeam === 1 ? team1Marker : team2Marker;
            await marker.addPoints(result.totalPoints);
            updateScoreMarkers();

            const typeName = result.winningDeclaration?.type === 'rondin' ? 'Rondín' : 'Ronda';
            const winningTeamColor = result.winningTeam === 1 ? TEAM_COLORS.team1.hex : TEAM_COLORS.team2.hex;

            // Add history entry for envío won
            const historyType = resolution === 'a_la_mayor' ? 'A la Mayor' : 'Envío Ganado';
            // Determine actor based on winning team
            const winningDeclarations = validDeclarations.filter(d => {
                const team = (d.playerIndex === 0 || d.playerIndex === 2) ? 1 : 2;
                return team === result.winningTeam;
            });
            let actor = 'Pareja Rival';
            if (winningDeclarations.some(d => d.playerIndex === 0) && winningDeclarations.some(d => d.playerIndex === 2)) {
                actor = 'Jugador y Compañero';
            } else if (winningDeclarations.some(d => d.playerIndex === 0)) {
                actor = 'Jugador';
            } else if (winningDeclarations.some(d => d.playerIndex === 2)) {
                actor = 'Compañero';
            }
            marker.addHistoryEntry(historyType, actor, result.totalPoints, handNumber);

            if (toastManagerRef.current) {
                if (resolution === 'a_la_mayor') {
                    toastManagerRef.current.showToast(`${TOAST_ICONS.A_LA_MAYOR} A la mayor\n${TOAST_ICONS.WINNER} ${formatTeam(result.winningTeam)}${SEPARATOR}${typeName}${SEPARATOR}${formatPoints(result.totalPoints)}`, {
                        type: 'team' + result.winningTeam,
                        teamColor: winningTeamColor,
                        duration: 3500,
                        position: 'center'
                    });
                } else {
                    toastManagerRef.current.showToast(`${TOAST_ICONS.ACCEPT} Envío aceptado\n${TOAST_ICONS.WINNER} ${formatTeam(result.winningTeam)}${SEPARATOR}${formatPoints(result.totalPoints)}`, {
                        type: 'team' + result.winningTeam,
                        teamColor: winningTeamColor,
                        duration: 3500,
                        position: 'center'
                    });
                }
            }
        }

        await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.envio.afterResolution));
        startNormalTurn(manoPlayerIndex);
    };

    // Check if enough medias have been revealed to determine the envío winner
    const checkAndResolveEnvioAfterMedias = async (playerIndex, cardValue, revealedCount, isLastMedia) => {
        // Only check if there's an accepted envío or 'a la mayor' pending resolution
        if (!rondaState.envioAccepted || (rondaState.resolution !== 'accepted' && rondaState.resolution !== 'a_la_mayor')) {
            return;
        }

        const validDeclarations = rondaState.validDeclarations || [];
        const manoPlayerIndex = rondaState.manoPlayerIndex;

        // Build updated revealed medias including the current reveal
        // (since React state update is async, rondaState.revealedMedias doesn't include current reveal yet)
        const updatedRevealedMedias = {
            ...rondaState.revealedMedias,
            [playerIndex]: revealedCount,
        };

        // Check if we can determine the winner yet
        // We need at least one media from each participant to compare
        // Or if we've revealed enough to know the winner (e.g., Reyes when going first)

        let canDetermineWinner = false;
        let winningDeclaration = null;
        let maxValue = -1;

        // Check all declarations and their revealed medias
        for (const decl of validDeclarations) {
            const revealed = updatedRevealedMedias[decl.playerIndex] || 0;

            if (revealed > 0) {
                // This player has revealed at least one media
                const typeWeight = decl.type === 'rondin' ? 1000 : 0;
                const effectiveValue = typeWeight + decl.value;

                if (effectiveValue > maxValue) {
                    maxValue = effectiveValue;
                    winningDeclaration = decl;
                } else if (effectiveValue === maxValue) {
                    // Tie: winner is whoever is closest to mano
                    const currentWinnerDistance = (winningDeclaration.playerIndex - manoPlayerIndex + 4) % 4;
                    const newDistance = (decl.playerIndex - manoPlayerIndex + 4) % 4;
                    if (newDistance < currentWinnerDistance) {
                        winningDeclaration = decl;
                    }
                }
            }
        }

        // Check if we have enough information to determine the winner
        // We need at least one media revealed from EACH team
        // AND we need the HIGHEST declaration from each team to have revealed their media
        const team1Declarations = validDeclarations.filter(d => d.playerIndex === 0 || d.playerIndex === 2);
        const team2Declarations = validDeclarations.filter(d => d.playerIndex === 1 || d.playerIndex === 3);

        const team1Revealed = team1Declarations.filter(d => (updatedRevealedMedias[d.playerIndex] || 0) > 0);
        const team2Revealed = team2Declarations.filter(d => (updatedRevealedMedias[d.playerIndex] || 0) > 0);

        // Find the best declaration for each team
        const getBestDeclaration = (declarations) => {
            return declarations.reduce((best, decl) => {
                const typeWeight = decl.type === 'rondin' ? 1000 : 0;
                const effectiveValue = typeWeight + decl.value;
                const bestTypeWeight = best ? (best.type === 'rondin' ? 1000 : 0) : -1;
                const bestEffectiveValue = best ? bestTypeWeight + best.value : -1;
                return effectiveValue > bestEffectiveValue ? decl : best;
            }, null);
        };

        const team1Best = getBestDeclaration(team1Declarations);
        const team2Best = getBestDeclaration(team2Declarations);

        // Check if the best declaration from each team has been revealed
        const team1BestRevealed = team1Best && (updatedRevealedMedias[team1Best.playerIndex] || 0) > 0;
        const team2BestRevealed = team2Best && (updatedRevealedMedias[team2Best.playerIndex] || 0) > 0;

        // We can determine the winner only if both teams' best declarations have been revealed
        // OR if the revealed media is clearly winning (e.g., team has only one declaration and it's been revealed)
        const team1OnlyOneDeclAndRevealed = team1Declarations.length === 1 && team1Revealed.length === 1;
        const team2OnlyOneDeclAndRevealed = team2Declarations.length === 1 && team2Revealed.length === 1;

        // Can determine if:
        // 1. Both teams' best have revealed, OR
        // 2. One team only has one declaration (revealed) and the other team's best has been revealed
        canDetermineWinner = (team1BestRevealed && team2BestRevealed) ||
                            (team1OnlyOneDeclAndRevealed && team2BestRevealed) ||
                            (team2OnlyOneDeclAndRevealed && team1BestRevealed);

        // Special cases where we can determine early:
        // - Reyes (12) revealed by a team and they're first in mano order
        if (team1Best && team1Best.value === 12 && team1BestRevealed) {
            // Team 1 has Reyes and revealed it
            const team1BestDistance = (team1Best.playerIndex - manoPlayerIndex + 4) % 4;
            const team2BestDistance = team2Best ? (team2Best.playerIndex - manoPlayerIndex + 4) % 4 : 999;
            if (team1BestDistance < team2BestDistance) {
                canDetermineWinner = true;
                winningDeclaration = team1Best;
            }
        }
        if (team2Best && team2Best.value === 12 && team2BestRevealed) {
            const team2BestDistance = (team2Best.playerIndex - manoPlayerIndex + 4) % 4;
            const team1BestDistance = team1Best ? (team1Best.playerIndex - manoPlayerIndex + 4) % 4 : 999;
            if (team2BestDistance < team1BestDistance) {
                canDetermineWinner = true;
                winningDeclaration = team2Best;
            }
        }

        // When we can determine the winner, compare the BEST declarations from each team
        if (canDetermineWinner && team1Best && team2Best) {
            const team1EffectiveValue = (team1Best.type === 'rondin' ? 1000 : 0) + team1Best.value;
            const team2EffectiveValue = (team2Best.type === 'rondin' ? 1000 : 0) + team2Best.value;

            if (team1EffectiveValue > team2EffectiveValue) {
                winningDeclaration = team1Best;
            } else if (team2EffectiveValue > team1EffectiveValue) {
                winningDeclaration = team2Best;
            } else {
                // Tie: winner is whoever's best is closest to mano
                const team1Distance = (team1Best.playerIndex - manoPlayerIndex + 4) % 4;
                const team2Distance = (team2Best.playerIndex - manoPlayerIndex + 4) % 4;
                winningDeclaration = team1Distance < team2Distance ? team1Best : team2Best;
            }
        }

        if (canDetermineWinner && winningDeclaration) {
            // Award points to the winning team
            const winningTeam = (winningDeclaration.playerIndex === 0 || winningDeclaration.playerIndex === 2) ? 1 : 2;

            // Calculate points based on resolution type
            let totalPoints;
            let messagePrefix;

            if (rondaState.resolution === 'a_la_mayor') {
                // "A la mayor": only count points from winning team's declarations
                const winningTeamDeclarations = validDeclarations.filter(d => {
                    const team = (d.playerIndex === 0 || d.playerIndex === 2) ? 1 : 2;
                    return team === winningTeam;
                });
                totalPoints = winningTeamDeclarations.reduce((sum, d) => sum + d.points, 0);
                messagePrefix = 'A la mayor:';
            } else {
                // "Accepted": winner gets all accumulated points
                totalPoints = validDeclarations.reduce((sum, d) => sum + d.points, 0);
                messagePrefix = '¡Envío ganado!';
            }

            const marker = winningTeam === 1 ? team1Marker : team2Marker;
            await marker.addPoints(totalPoints);
            updateScoreMarkers();

            const typeName = winningDeclaration.type === 'rondin' ? 'Rondín' : 'Ronda';
            const cardName = rondaLogicRef.current.getCardValueName(winningDeclaration.value);
            const winningTeamColor = winningTeam === 1 ? TEAM_COLORS.team1.hex : TEAM_COLORS.team2.hex;

            // Add history entry for envío won after medias revealed
            const historyType = rondaState.resolution === 'a_la_mayor' ? 'A la Mayor' : 'Envío Ganado';
            const winningTeamDeclarations = validDeclarations.filter(d => {
                const team = (d.playerIndex === 0 || d.playerIndex === 2) ? 1 : 2;
                return team === winningTeam;
            });
            let actor = 'Pareja Rival';
            if (winningTeamDeclarations.some(d => d.playerIndex === 0) && winningTeamDeclarations.some(d => d.playerIndex === 2)) {
                actor = 'Jugador y Compañero';
            } else if (winningTeamDeclarations.some(d => d.playerIndex === 0)) {
                actor = 'Jugador';
            } else if (winningTeamDeclarations.some(d => d.playerIndex === 2)) {
                actor = 'Compañero';
            }
            marker.addHistoryEntry(historyType, actor, totalPoints, handNumber);

            if (toastManagerRef.current) {
                toastManagerRef.current.showToast(`${messagePrefix}\n${TOAST_ICONS.WINNER} ${formatTeam(winningTeam)}${SEPARATOR}${typeName} de ${cardName}${SEPARATOR}${formatPoints(totalPoints)}`, {
                    type: 'team' + winningTeam,
                    teamColor: winningTeamColor,
                    duration: 4000,
                    position: 'center'
                });
            }

            debugLog(`🏆 Envío resolved (${rondaState.resolution}): Team ${winningTeam} wins with ${typeName} of ${cardName}. +${totalPoints} points`);

            // Clear envío state
            setRondaState(prev => ({
                ...prev,
                envioAccepted: false,
                resolution: null,
            }));
        }
    };

    // Handle player 1's decision on Ronda/Rondín
    const handlePlayer1RondaDecision = async (decision) => {
        debugLog(`👤 Player 0 decides: ${decision}`);

        const playerName = players[0]?.name || 'Jugador 1';

        if (decision === 'cantar') {
            // Announce the Ronda/Rondín - NO revelar el valor, solo el tipo
            const decl = rondaState.player1Declaration;
            const typeName = decl.type === 'rondin' ? 'Rondín' : 'Ronda';

            if (toastManagerRef.current) {
                toastManagerRef.current.showPlayerToast(`${TOAST_ICONS.DECLARATION} ${typeName}`, 0, {
                    type: 'team1',
                    teamColor: TEAM_COLORS.team1.hex,
                    duration: 3500
                });
            }

            // Trigger sound and vibration feedback for human player
            if (decl.type === 'rondin') {
                soundVibrationService.triggerRondin();
            } else {
                soundVibrationService.triggerRonda();
            }

            // Check if we're in the announcement phase (separated from envío)
            if (rondaState.phase === 'announcement' && rondaState.announcedByTeam) {
                // We're in the new announcement flow
                debugLog('🎤 Player 0 announced in announcement phase');

                // Add player 0 to announced players for team 1
                const updatedAnnouncedByTeam = {
                    ...rondaState.announcedByTeam,
                    team1: [...rondaState.announcedByTeam.team1, 0]
                };

                setRondaState(prev => ({
                    ...prev,
                    showButtons: false,
                    envioHistory: [...prev.envioHistory, { playerIndex: 0, action: 'cantar' }],
                    announcedByTeam: updatedAnnouncedByTeam,
                }));

                await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.ronda.afterPointsAwarded));

                // Continue with remaining announcements if any
                const pendingAnnouncements = rondaState.pendingAnnouncementsAfterPlayer0 || [];

                if (pendingAnnouncements.length > 0) {
                    // There are more players to announce after player 0
                    for (const decl of pendingAnnouncements) {
                        if (decl.playerIndex !== 0) {
                            const typeName = decl.type === 'rondin' ? 'Rondín' : 'Ronda';
                            const playerName = players[decl.playerIndex]?.name || `Jugador ${decl.playerIndex + 1}`;
                            const playerTeam = (decl.playerIndex === 0 || decl.playerIndex === 2) ? 'team1' : 'team2';

                            await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.ronda.betweenAnnouncements));

                            if (toastManagerRef.current) {
                                const teamId = decl.playerIndex % 2 === 0 ? 1 : 2;
                                const teamColor = teamId === 1 ? TEAM_COLORS.team1.hex : TEAM_COLORS.team2.hex;
                                toastManagerRef.current.showPlayerToast(`${TOAST_ICONS.DECLARATION} ${typeName}`, decl.playerIndex, {
                                    type: 'team' + teamId,
                                    teamColor: teamColor,
                                    duration: 3000
                                });
                            }

                            // Trigger sound and vibration feedback
                            if (decl.type === 'rondin') {
                                soundVibrationService.triggerRondin();
                            } else {
                                soundVibrationService.triggerRonda();
                            }

                            debugLog(`🎺 ${playerName} (Player ${decl.playerIndex}) announces ${typeName}`);
                            updatedAnnouncedByTeam[playerTeam].push(decl.playerIndex);
                        }
                    }
                }

                // Now finish the announcement phase
                await finishAnnouncementPhaseFromState(updatedAnnouncedByTeam);
            } else {
                // Legacy flow - we're already in envío phase
                setRondaState(prev => ({
                    ...prev,
                    envioHistory: [...prev.envioHistory, { playerIndex: 0, action: 'cantar' }],
                }));

                await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.ronda.afterPointsAwarded));

                if (rondaState.needsEnvio) {
                    const manoPlayerIndex = rondaState.manoPlayerIndex;
                    const hasPendingEnvio = rondaState.pendingEnvio !== null;
                    const availableDecisions = getAvailableDecisions(0, rondaState.envioParticipants, hasPendingEnvio, manoPlayerIndex);

                    setRondaState(prev => ({
                        ...prev,
                        phase: 'envio',
                        showButtons: true,
                        availableDecisions,
                    }));
                } else {
                    await awardRondaPoints(rondaState.validDeclarations);
                    startNormalTurn(rondaState.manoPlayerIndex);
                }
            }
        } else if (decision === 'no_cantar') {
            // Player 0 chooses not to announce their ronda
            if (toastManagerRef.current) {
                toastManagerRef.current.showPlayerToast(`${TOAST_ICONS.PASO} Paso`, 0, {
                    type: 'info',
                    duration: 2500
                });
            }

            debugLog('🙅 Player 0 chooses not to announce ronda');

            // Check if we're in the announcement phase
            if (rondaState.phase === 'announcement' && rondaState.announcedByTeam) {
                // New announcement flow - player 0 passed, continue with remaining
                const updatedAnnouncedByTeam = { ...rondaState.announcedByTeam };

                setRondaState(prev => ({
                    ...prev,
                    showButtons: false,
                    player1Declaration: null,
                }));

                await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.envio.playerActionDelay));

                // Continue with remaining announcements
                const pendingAnnouncements = rondaState.pendingAnnouncementsAfterPlayer0 || [];

                for (const decl of pendingAnnouncements) {
                    if (decl.playerIndex !== 0) {
                        const typeName = decl.type === 'rondin' ? 'Rondín' : 'Ronda';
                        const playerName = players[decl.playerIndex]?.name || `Jugador ${decl.playerIndex + 1}`;
                        const playerTeam = (decl.playerIndex === 0 || decl.playerIndex === 2) ? 'team1' : 'team2';

                        await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.ronda.betweenAnnouncements));

                        if (toastManagerRef.current) {
                            const teamId = decl.playerIndex % 2 === 0 ? 1 : 2;
                            const teamColor = teamId === 1 ? TEAM_COLORS.team1.hex : TEAM_COLORS.team2.hex;
                            toastManagerRef.current.showPlayerToast(`${TOAST_ICONS.DECLARATION} ${typeName}`, decl.playerIndex, {
                                type: 'team' + teamId,
                                teamColor: teamColor,
                                duration: 3000
                            });
                        }

                        debugLog(`🎺 ${playerName} (Player ${decl.playerIndex}) announces ${typeName}`);
                        updatedAnnouncedByTeam[playerTeam].push(decl.playerIndex);
                    }
                }

                // Finish announcement phase (player 0 is NOT included since they passed)
                await finishAnnouncementPhaseFromState(updatedAnnouncedByTeam);
            } else {
                // Legacy flow
                const updatedValidDeclarations = rondaState.validDeclarations.filter(d => d.playerIndex !== 0);
                const updatedEnvioParticipants = rondaState.envioParticipants.filter(p => p !== 0);

                setRondaState(prev => ({
                    ...prev,
                    validDeclarations: updatedValidDeclarations,
                    envioParticipants: updatedEnvioParticipants,
                    player1Declaration: null,
                    showButtons: false,
                    envioHistory: [...prev.envioHistory, { playerIndex: 0, action: 'no_cantar' }],
                }));

                await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.envio.playerActionDelay));

                if (updatedValidDeclarations.length === 0) {
                    debugLog('No declarations remaining after player 0 passed');
                    startNormalTurn(rondaState.manoPlayerIndex);
                } else {
                    const reprocessed = rondaLogicRef.current.processDeclarations(updatedValidDeclarations);

                    if (reprocessed.needsEnvio && updatedEnvioParticipants.length > 1) {
                        const manoPlayerIndex = rondaState.manoPlayerIndex;
                        const sortedParticipants = [...updatedEnvioParticipants].sort((a, b) => {
                            const distA = (a - manoPlayerIndex + 4) % 4;
                            const distB = (b - manoPlayerIndex + 4) % 4;
                            return distA - distB;
                        });

                        const nextParticipant = sortedParticipants[0];

                        if (nextParticipant !== 0) {
                            // allPlayersAnnounced=true since remaining players have announced
                            await processEnvioForAI(nextParticipant, updatedValidDeclarations, updatedEnvioParticipants, null, manoPlayerIndex, true);
                        }
                    } else {
                        await awardRondaPoints(updatedValidDeclarations);
                        startNormalTurn(rondaState.manoPlayerIndex);
                    }
                }
            }
        } else if (decision === 'enviar') {
            if (toastManagerRef.current) {
                // Show toast near player 1's position with team color
                toastManagerRef.current.showPlayerToast(`${TOAST_ICONS.ENVIO} Envío`, 0, {
                    type: 'team1',
                    teamColor: TEAM_COLORS.team1.hex,
                    duration: 3500
                });
            }

            setRondaState(prev => ({
                ...prev,
                pendingEnvio: { from: 0 },
                envioHistory: [...prev.envioHistory, { playerIndex: 0, action: 'enviar' }],
                showButtons: false,
                envioAccepted: false,
            }));

            // After sending, find the next player from the opposing team with ronda
            await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.envio.playerActionDelay));

            const manoPlayerIndex = rondaState.manoPlayerIndex;
            const senderTeam = 1; // Player 0 is team 1
            const sortedParticipants = [...rondaState.envioParticipants].sort((a, b) => {
                const distA = (a - manoPlayerIndex + 4) % 4;
                const distB = (b - manoPlayerIndex + 4) % 4;
                return distA - distB;
            });

            // Find next opponent with ronda in turn order
            let nextOpponent = null;
            const currentIdx = sortedParticipants.indexOf(0);
            for (let i = 1; i <= sortedParticipants.length; i++) {
                const nextIdx = (currentIdx + i) % sortedParticipants.length;
                const candidatePlayer = sortedParticipants[nextIdx];
                const candidateTeam = (candidatePlayer === 0 || candidatePlayer === 2) ? 1 : 2;
                if (candidateTeam !== senderTeam) {
                    nextOpponent = candidatePlayer;
                    break;
                }
            }

            if (nextOpponent !== null) {
                // Pass 0 (player 0's index) as pendingEnvioFrom so opponent knows there's a pending envío
                // allPlayersAnnounced=true since we're in envío phase
                await processEnvioForAI(nextOpponent, rondaState.validDeclarations, rondaState.envioParticipants, 0, rondaState.manoPlayerIndex, true);
            }

        } else if (decision === 'chiquita') {
            if (toastManagerRef.current) {
                // Show toast near player 0's position
                toastManagerRef.current.showPlayerToast(`${TOAST_ICONS.CHIQUITA} Chiquita`, 0, {
                    type: 'info',
                    duration: 3500
                });
            }

            setRondaState(prev => ({
                ...prev,
                envioHistory: [...prev.envioHistory, { playerIndex: 0, action: 'chiquita' }],
                showButtons: false,
            }));

            // Pass to next player with declaration in turn order
            await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.envio.playerActionDelay));

            // Find the next participant in turn order after player 0
            const manoPlayerIndex = rondaState.manoPlayerIndex;
            const sortedParticipants = [...rondaState.envioParticipants].sort((a, b) => {
                const distA = (a - manoPlayerIndex + 4) % 4;
                const distB = (b - manoPlayerIndex + 4) % 4;
                return distA - distB;
            });
            const currentIndex = sortedParticipants.indexOf(0);
            const nextParticipant = sortedParticipants[(currentIndex + 1) % sortedParticipants.length];

            if (nextParticipant !== 0) {
                // allPlayersAnnounced=true since we're in envío phase
                await processEnvioForAI(nextParticipant, rondaState.validDeclarations, rondaState.envioParticipants, null, manoPlayerIndex, true);
            }

        } else if (decision === 'acepto') {
            if (toastManagerRef.current) {
                // Show toast near player 1's position with team color
                toastManagerRef.current.showPlayerToast(`${TOAST_ICONS.ACCEPT} Acepto`, 0, {
                    type: 'team1',
                    teamColor: TEAM_COLORS.team1.hex,
                    duration: 3500
                });
            }

            // Mark envío as accepted - points will be awarded when medias are revealed
            setRondaState(prev => ({
                ...prev,
                showButtons: false,
                envioAccepted: true,
                resolution: 'accepted',
            }));

            // Start normal play - the winner will be determined when medias are revealed
            if (toastManagerRef.current) {
                toastManagerRef.current.showToast(`${TOAST_ICONS.ACCEPT} Envío aceptado${SEPARATOR}Se determina al ver las medias`, {
                    type: 'info',
                    duration: 4000,
                    position: 'center'
                });
            }
            await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.envio.aiDecisionDelay));
            startNormalTurn(rondaState.manoPlayerIndex);

        } else if (decision === 'no_acepto') {
            if (toastManagerRef.current) {
                // Show toast near player 1's position with team color
                toastManagerRef.current.showPlayerToast(`${TOAST_ICONS.REJECT} No acepto`, 0, {
                    type: 'team1',
                    teamColor: TEAM_COLORS.team1.hex,
                    duration: 3500
                });
            }

            setRondaState(prev => ({ ...prev, showButtons: false }));
            await resolveEnvio('declined', rondaState.validDeclarations, rondaState.manoPlayerIndex);

        } else if (decision === 'a_la_mayor') {
            if (toastManagerRef.current) {
                // Show toast near player 1's position with team color
                toastManagerRef.current.showPlayerToast(`${TOAST_ICONS.A_LA_MAYOR} A la mayor`, 0, {
                    type: 'team1',
                    teamColor: TEAM_COLORS.team1.hex,
                    duration: 3500
                });
            }

            // Mark as "a la mayor" - winner will be determined when medias are revealed
            // But only their own ronda points count, not accumulated
            setRondaState(prev => ({
                ...prev,
                showButtons: false,
                envioAccepted: true,
                resolution: 'a_la_mayor',
            }));

            if (toastManagerRef.current) {
                toastManagerRef.current.showToast(`${TOAST_ICONS.A_LA_MAYOR} A la mayor${SEPARATOR}Se determina al ver las medias`, {
                    type: 'info',
                    duration: 4000,
                    position: 'center'
                });
            }
            await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.envio.aiDecisionDelay));
            startNormalTurn(rondaState.manoPlayerIndex);
        }
    };

    // Handle turn changes
    useEffect(() => {
        const processTurnAsync = async () => {
            // Don't process turns during Ronda/Rondín detection phase
            if (rondaState.phase !== 'none' && rondaState.phase !== undefined) {
                debugLog(`⏸️ Turn effect skipped - in Ronda phase: ${rondaState.phase}`);
                return;
            }

            // Don't allow card interaction during envío resolution - wait for accept/reject decision
            if (rondaState.needsEnvio && !rondaState.resolution) {
                debugLog('⏸️ Turn effect skipped - waiting for envío resolution');
                // Disable card interaction while waiting
                if (dealManagerRendererRef.current) {
                    dealManagerRendererRef.current.disablePlayerCardsInteractivity(0);
                }
                return;
            }

            // Don't process turns if there's an active caída opportunity for the human player
            if (caidaOpportunity.active) {
                debugLog('⏸️ Turn effect skipped - waiting for caída decision');
                return;
            }

            // Wait if game is paused
            if (isPausedRef.current) {
                debugLog('⏸️ Game is paused, waiting to unpause before processing turn...');
                await waitForUnpause();
            }

            if (currentTurn !== null && dealManagerRendererRef.current && !isProcessingTurn) {
                const playerIndex = currentTurn;
                const isHuman = playerIndex === 0; // Assuming Player 1 (index 0) is always human
                debugLog(`🔄 Turn effect triggered: Player ${playerIndex} (${isHuman ? 'Human' : 'AI'}), isProcessingTurn: ${isProcessingTurn}`);

                if (isHuman) {
                    // Enable interaction for human player
                    dealManagerRendererRef.current.makePlayerCardsInteractive(playerIndex, (card, sprite) => {
                        handleCardPlay(playerIndex, card, sprite);
                    });
                } else {
                    // AI Turn
                    // Disable interaction for human while AI plays
                    dealManagerRendererRef.current.disablePlayerCardsInteractivity(0);

                    // Simulate AI thinking time
                    setTimeout(async () => {
                        // Check again if paused before processing AI turn
                        if (isPausedRef.current) {
                            debugLog('⏸️ Game paused before AI turn, waiting to unpause...');
                            await waitForUnpause();
                        }
                        processAiTurn(playerIndex);
                    }, GAME_TIMINGS.ai.thinkingTime);
                }
            }
        };

        processTurnAsync();
    }, [currentTurn, isProcessingTurn, rondaState.phase, rondaState.needsEnvio, rondaState.resolution, caidaOpportunity.active, isPausedRef, waitForUnpause]);

    // AI Turn Logic (delegates to AIService)
    const processAiTurn = (playerIndex) => {
        if (!dealManagerRef.current || !rondaLogicRef.current) return;

        const playerCards = dealManagerRef.current.getPlayerCards(playerIndex);
        if (playerCards.length === 0) {
            debugLog(`Player ${playerIndex} has no cards! Skipping to next player...`);
            const nextTurnIndex = rondaLogicRef.current.getNextTurn(playerIndex);
            setCurrentTurn(nextTurnIndex);
            setIsProcessingTurn(false);
            return;
        }

        const centerCards = dealManagerRef.current.getCenterCards();

        // Build game state for AI decision
        const dealerPlayerIndex = startingDealer ? players.findIndex(p => p.id === startingDealer.id) : -1;
        const isLastHand = deckRef.current ? deckRef.current.remaining === 0 : false;

        // Extract team ronda value (if partner has a declared ronda)
        const playerTeam = (playerIndex === 0 || playerIndex === 2) ? 1 : 2;
        const teamDeclarations = rondaState.validDeclarations?.filter(d => {
            const dTeam = (d.playerIndex === 0 || d.playerIndex === 2) ? 1 : 2;
            return dTeam === playerTeam && d.playerIndex !== playerIndex;
        }) || [];
        const teamRondaValue = teamDeclarations.length > 0 ? teamDeclarations[0].value : null;

        const gameState = {
            lastDroppedValue: lastDroppedValueRef.current,
            dealerIndex: dealerPlayerIndex,
            isLastHand,
            teamRondaValue,
        };

        const decision = aiServiceRef.current.decideCardToPlay(playerIndex, playerCards, centerCards, gameState);
        const cardToPlay = decision.card;

        debugLog(`🤖 AI Player ${playerIndex} decision: ${decision.reason}`, cardToPlay);

        // Find the sprite for this card
        const sprites = dealManagerRendererRef.current.getPlayerCardSprites(playerIndex);
        const sprite = sprites.find(s => s.cardData.suit === cardToPlay.suit && s.cardData.value === cardToPlay.value);

        if (sprite) {
            handleCardPlay(playerIndex, cardToPlay, sprite);
        }
    };

    // Handle card play (Human or AI)
    const handleCardPlay = async (playerIndex, card, cardSprite) => {
        if (isProcessingTurn) return;
        setIsProcessingTurn(true);

        // Reset card scale and position if it was hovered (for human player)
        if (cardSprite._originalScale) {
            cardSprite.scale.set(cardSprite._originalScale);
            cardSprite.y = cardSprite._originalY;
            cardSprite.zIndex = 0;
        }

        // Disable interaction immediately
        dealManagerRendererRef.current.disablePlayerCardsInteractivity(0);

        debugLog(`Player ${playerIndex} played:`, card);

        // Check if this card is part of the player's Ronda/Rondín (media)
        const playerDeclaration = rondaState.validDeclarations?.find(d => d.playerIndex === playerIndex);
        debugLog(`[Media Check] Player ${playerIndex} - Card value: ${card.value}, Declaration:`, playerDeclaration);

        // Check if player actually announced (cantó) their ronda
        // If there was an envío phase, the player should be in envioHistory with action 'cantar'
        // If no envío was needed (only one team has ronda), the declaration is automatically valid
        const playerAnnounced = !rondaState.envioHistory?.length ||
                                rondaState.envioHistory.some(h => h.playerIndex === playerIndex && h.action === 'cantar');
        debugLog(`[Media Check] Player ${playerIndex} - Announced: ${playerAnnounced}, EnvioHistory:`, rondaState.envioHistory);

        if (playerDeclaration && playerAnnounced && playerDeclaration.value === card.value) {
            // This is a "media" - but only announce it if envío was accepted or there's no envío
            const playerName = players[playerIndex]?.name || `Jugador ${playerIndex + 1}`;
            const cardName = rondaLogicRef.current.getCardValueName(card.value);

            // Track revealed medias
            const revealedCount = (rondaState.revealedMedias?.[playerIndex] || 0) + 1;
            const isLastMedia = revealedCount >= (playerDeclaration.type === 'rondin' ? 3 : 2);

            // Show "media" message logic:
            // - If only one player has ronda (no envío): show it
            // - If envío was accepted or "a la mayor": show it
            // - If envío was declined: ONLY show it for the team that SENT (not for the team that declined)
            const playerTeam = (playerIndex === 0 || playerIndex === 2) ? 1 : 2;
            const shouldShowMedia = rondaState.resolution !== 'declined' ||
                                   (rondaState.resolution === 'declined' && playerTeam !== rondaState.decliningTeam);

            if (shouldShowMedia) {
                const mediaMessage = isLastMedia ? 'La otra media' : 'Media';

                if (toastManagerRef.current) {
                    toastManagerRef.current.showPlayerToast(`${TOAST_ICONS.MEDIA} ${mediaMessage}${SEPARATOR}${cardName}`, playerIndex, {
                        type: 'warning',
                        duration: 2500
                    });
                }
                debugLog(`🎴 ${playerName} reveals ${mediaMessage}: ${cardName}`);
            }

            // Update revealed medias count
            setRondaState(prev => ({
                ...prev,
                revealedMedias: {
                    ...prev.revealedMedias,
                    [playerIndex]: revealedCount,
                },
            }));

            // Check if all medias have been revealed and we need to resolve the envío
            await checkAndResolveEnvioAfterMedias(playerIndex, card.value, revealedCount, isLastMedia);
        } else if (playerDeclaration && playerAnnounced && playerDeclaration.value !== card.value) {
            // Player has a ronda AND announced it, but is playing a card that's NOT part of it - show "Además"
            const cardName = rondaLogicRef.current.getCardValueName(card.value);
            // Same logic as media: show UNLESS player is from the team that declined
            const playerTeam = (playerIndex === 0 || playerIndex === 2) ? 1 : 2;
            const shouldShowAdemas = rondaState.resolution !== 'declined' ||
                                    (rondaState.resolution === 'declined' && playerTeam !== rondaState.decliningTeam);

            if (shouldShowAdemas && toastManagerRef.current) {
                toastManagerRef.current.showPlayerToast(`${TOAST_ICONS.CARD} Además${SEPARATOR}${cardName}`, playerIndex, {
                    type: 'info',
                    duration: 2500
                });
            }
        }

        // 1. Remove card from player's hand (Logic)
        dealManagerRef.current.removePlayerCard(playerIndex, card);

        // 2. Process move using RondaLogic
        const centerCards = dealManagerRef.current.getCenterCards();
        debugLog(`[handleCardPlay] Player ${playerIndex} playing:`, card.suit, card.value);
        debugLog(`[handleCardPlay] Center cards BEFORE move:`, centerCards.map(c => `${c.suit} ${c.value}`));

        const result = rondaLogicRef.current.processMove(card, centerCards);

        // Track card for HARD mode AI
        if (aiServiceRef.current) {
            aiServiceRef.current.cardTracker.recordCard(card);
        }

        debugLog('Move result:', result);

        // If AI player (not player 0), flip the card first to reveal it
        if (playerIndex !== 0 && !cardSprite.isFaceUp && cardSprite && !cardSprite.destroyed) {
            const { Assets } = await import('pixi.js');
            const CardAnimations = (await import('../game/CardAnimations')).default;

            const faceUpTexture = await Assets.load(
                dealManagerRendererRef.current.getCardImagePath(card.suit, card.value)
            );

            await CardAnimations.flipCard(cardSprite, faceUpTexture, 0);
            if (cardSprite && !cardSprite.destroyed) {
                cardSprite.isFaceUp = true;
            }
        }

        if (result.action === 'DROP') {
            // Card goes to center
            dealManagerRef.current.addCenterCards([card]);

            // Update tracking state (Ref + State)
            lastDroppedValueRef.current = card.value;
            consecutiveDropCountRef.current = 1; // Drop starts a new chain of 1

            setLastDroppedValue(card.value);
            setConsecutiveDropCount(1);

            // Animate drop (uses slot system)
            await dealManagerRendererRef.current.animateCardDrop(playerIndex, cardSprite, 0);
        }

        // Track who collected in this turn synchronously (for end-of-round logic)
        // This needs to be outside the if/else so we can use it after processing
        let currentCollector = lastCollector;

        if (result.action === 'COLLECT') {
            // Cards are collected with sequential animation
            const centerSprites = dealManagerRendererRef.current.getCenterCardSprites();

            // Build array of matched cards with sprites
            const matchedCards = result.centerCardsToRemove.map(cardData => {
                const sprite = centerSprites.find(s =>
                    s.cardData.suit === cardData.suit && s.cardData.value === cardData.value
                );
                return { card: cardData, sprite };
            }).filter(item => item.sprite); // Remove any not found

            if (matchedCards.length === 0) {
                console.error('No matched card sprites found!');
                setCurrentTurn(rondaLogicRef.current.getNextTurn(playerIndex));
                setIsProcessingTurn(false);
                return;
            }

            // Check if this is a Caída (COLLECT where played card matches lastDroppedValue)
            const isCaida = lastDroppedValueRef.current !== null && card.value === lastDroppedValueRef.current;
            const currentCaidaCount = isCaida ? consecutiveDropCountRef.current + 1 : 0;

            debugLog(`Check Caída: isCaida=${isCaida}, prevCount=${consecutiveDropCountRef.current}, newCount=${currentCaidaCount}, card=${card.value}, lastDropped=${lastDroppedValueRef.current}`);

            // Check if next player can do contracaída/san vicente BEFORE collecting
            const nextTurnIndex = rondaLogicRef.current.getNextTurn(playerIndex);
            let canNextPlayerContra = false;

            if (isCaida && currentCaidaCount < 4) {
                // Check if next player has a card of the same value
                const nextPlayerCards = dealManagerRef.current?.getPlayerCards(nextTurnIndex) || [];
                const nextPlayerMatchingCards = nextPlayerCards.filter(c => c.value === card.value);
                canNextPlayerContra = nextPlayerMatchingCards.length > 0;

                if (canNextPlayerContra) {
                    debugLog(`⏸️ Caída detected! Next player (${nextTurnIndex}) can do contracaída. Waiting...`);
                }
            }

            // Determine collection pile position
            if (!appRef.current || !appRef.current.screen) {
                console.warn('[Ronda] Cannot determine pile position - app or screen is null');
                return;
            }
            const screenWidth = appRef.current.screen.width;
            const screenHeight = appRef.current.screen.height;
            const cardWidth = dealManagerRendererRef.current.cardWidth;
            const cardHeight = dealManagerRendererRef.current.cardHeight;
            const pileMargin = 80;

            let pilePos = { x: 0, y: 0 };
            switch (playerIndex) {
                case 0:
                    pilePos = {
                        x: screenWidth / 2 + cardWidth * 2,
                        y: screenHeight - pileMargin
                    };
                    break;
                case 1:
                    pilePos = {
                        x: screenWidth - pileMargin,
                        y: screenHeight / 2 + cardHeight
                    };
                    break;
                case 2:
                    pilePos = {
                        x: screenWidth / 2 - cardWidth * 2,
                        y: pileMargin
                    };
                    break;
                case 3:
                    pilePos = {
                        x: pileMargin,
                        y: screenHeight / 2 - cardHeight
                    };
                    break;
            }

            // If next player can do contracaída, pause before collecting
            if (canNextPlayerContra) {
                // First, animate the played card moving to center (but don't collect yet)
                // Use animateCardDrop to move it to the center area
                await dealManagerRendererRef.current.animateCardDrop(playerIndex, cardSprite, 0);

                // Add this card to center cards temporarily (so the next player can collect it too)
                dealManagerRef.current.addCenterCards([card]);

                // IMPORTANT: Update Refs to reflect the Caída happened, so next player sees the incremented count
                lastDroppedValueRef.current = card.value;
                consecutiveDropCountRef.current = currentCaidaCount;
                setLastDroppedValue(card.value);
                setConsecutiveDropCount(currentCaidaCount);

                // Determine what type the contracaída would be
                let potentialCaidaType = 'contra';
                if (currentCaidaCount + 1 === 4) potentialCaidaType = 'san_vicente';

                // Show caída opportunity for the next player
                if (nextTurnIndex === 0) {
                    // Human player's turn - show buttons
                    const humanCards = dealManagerRef.current?.getPlayerCards(0) || [];
                    const humanMatchingCards = humanCards.filter(c => c.value === card.value);

                    setCaidaOpportunity({
                        active: true,
                        cardValue: card.value,
                        caidaType: potentialCaidaType,
                        matchingCards: humanMatchingCards,
                        nextTurnIndex: nextTurnIndex,
                        // Store pending collection info so we can complete it after decision
                        pendingCollection: {
                            collectorIndex: playerIndex,
                            cardSprite: cardSprite,
                            matchedCards: matchedCards,
                            pilePos: pilePos,
                            result: result,
                            currentCaidaCount: currentCaidaCount,
                        },
                        timeoutId: null,
                    });

                    // Enable only the matching cards for the human player
                    dealManagerRendererRef.current.makePlayerCardsInteractive(0, (clickedCard, sprite) => {
                        if (clickedCard.value === card.value) {
                            handleCaidaDecision('caer');
                        }
                    });

                    setIsProcessingTurn(false);
                    return; // Wait for human decision
                } else {
                    // AI player's turn - let them decide
                    const aiCards = dealManagerRef.current?.getPlayerCards(nextTurnIndex) || [];
                    const aiMatchingCards = aiCards.filter(c => c.value === card.value);

                    // AI decides whether to take contracaída via AIService
                    const { shouldContracaida, card: caidaCard } = aiServiceRef.current.decideContracaida(
                        nextTurnIndex, aiMatchingCards, {}
                    );

                    if (shouldContracaida && caidaCard) {
                        debugLog(`🤖 AI Player ${nextTurnIndex} will do contracaída!`);

                        // Wait a moment to show the AI is "thinking"
                        await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.ai.thinkingTime));

                        // AI plays the contracaída card
                        const aiSprites = dealManagerRendererRef.current.getPlayerCardSprites(nextTurnIndex);
                        const aiSprite = aiSprites.find(s =>
                            s.cardData.suit === caidaCard.suit && s.cardData.value === caidaCard.value
                        );

                        if (aiSprite) {
                            // Don't complete current collection - let the AI play and they will collect
                            setCurrentTurn(nextTurnIndex);
                            setIsProcessingTurn(false);
                            return;
                        }
                    }
                }
            }

            // No contracaída opportunity or it was passed - proceed with normal collection

            // Check for Caída on COLLECT (when played card matches lastDroppedValue)
            // Calculate this BEFORE the animation so we can trigger feedback first
            let caidaPoints = 0;
            let caidaType = null;
            if (isCaida) {
                if (currentCaidaCount === 2) {
                    caidaPoints = 1;
                    caidaType = 'Caída';
                } else if (currentCaidaCount === 3) {
                    caidaPoints = 3; // FIXED: Contracaída is 3 points total (winner takes all)
                    caidaType = 'Contracaída';
                } else if (currentCaidaCount === 4) {
                    caidaPoints = 4; // FIXED: San Vicente is 4 points total
                    caidaType = 'San Vicente';
                }
            }

            // Trigger Caída sound and vibration feedback BEFORE the animation
            if (caidaType) {
                soundVibrationService.triggerCaida();
            }

            await dealManagerRendererRef.current.animateSequentialCollection(
                cardSprite,
                matchedCards,
                pilePos
            );

            // Remove cards from center (Logic)
            const centerCardsBefore = dealManagerRef.current.getCenterCards();
            debugLog(`🧹 [Aseo Debug] Center cards BEFORE removal: ${centerCardsBefore.length}`,
                centerCardsBefore.map(c => `${c.value}${c.suit}`));
            debugLog(`🧹 [Aseo Debug] Cards to remove: ${result.centerCardsToRemove.length}`,
                result.centerCardsToRemove.map(c => `${c.value}${c.suit}`));

            result.centerCardsToRemove.forEach(c => dealManagerRef.current.removeCenterCard(c));

            // Check for Aseo (player cleared the board)
            const centerCardsAfterRemoval = dealManagerRef.current.getCenterCards();
            debugLog(`🧹 [Aseo Debug] Center cards AFTER removal: ${centerCardsAfterRemoval.length}`,
                centerCardsAfterRemoval.map(c => `${c.value}${c.suit}`));
            const isAseo = centerCardsAfterRemoval.length === 0;

            // Validate if Aseo is valid according to rules
            // Rule: Aseo is ONLY invalid when dealer plays their LAST card in the LAST hand (last card of the game)
            const isLastHand = deckRef.current.remaining === 0;
            const dealerPlayerIndex = startingDealer ? players.findIndex(p => p.id === startingDealer.id) : -1;
            const isDealerLastToPlay = playerIndex === dealerPlayerIndex;

            // Check if all other players have played their last card (only dealer has cards left or just played)
            const playerCardsAfterPlay = dealManagerRef.current.getPlayerCards(playerIndex).length;
            const allOtherPlayersEmpty = [0, 1, 2, 3]
                .filter(i => i !== playerIndex)
                .every(i => dealManagerRef.current.getPlayerCards(i).length === 0);

            // Aseo is invalid ONLY if: last hand + dealer is playing + it's dealer's LAST card + all others already played
            // The key is: dealer must be playing their LAST card (playerCardsAfterPlay === 0)
            const isAseoValid = !(isLastHand && isDealerLastToPlay && allOtherPlayersEmpty && playerCardsAfterPlay === 0);

            debugLog(`🧹 [Aseo Check] isAseo=${isAseo}, centerCardsRemaining=${centerCardsAfterRemoval.length}, ` +
                `isLastHand=${isLastHand}, dealerIdx=${dealerPlayerIndex}, playerIdx=${playerIndex}, ` +
                `isDealerLastToPlay=${isDealerLastToPlay}, allOthersEmpty=${allOtherPlayersEmpty}, ` +
                `playerCardsAfterPlay=${playerCardsAfterPlay}, isAseoValid=${isAseoValid}`);

            const aseoTeamId = playerIndex % 2 === 0 ? 1 : 2;
            const marker = aseoTeamId === 1 ? team1Marker : team2Marker;
            const aseoTeamColor = aseoTeamId === 1 ? TEAM_COLORS.team1.hex : TEAM_COLORS.team2.hex;

            if (isAseo && isAseoValid && caidaType) {
                // Combo: Aseo + Caída/Contracaída/San Vicente
                const totalPoints = 1 + caidaPoints; // 1 for Aseo + caída points
                const comboMessage = `Aseo + ${caidaType}`;
                debugLog(`🧹🎯 ${comboMessage}! Player ${playerIndex}. +${totalPoints} points for team${aseoTeamId}`);
                if (toastManagerRef.current) {
                    const caidaIcon = caidaType === 'Caída' ? TOAST_ICONS.CAIDA_3 : caidaType === 'Contracaída' ? TOAST_ICONS.CAIDA_4 : '💫';
                    toastManagerRef.current.showPlayerToast(`${TOAST_ICONS.ASEO} Aseo + ${caidaIcon} ${caidaType}`, playerIndex, {
                        type: 'team' + aseoTeamId,
                        teamColor: aseoTeamColor,
                        duration: 3000
                    });
                }
                // Sound and vibration feedback already triggered before animation
                await marker.addPoints(totalPoints);
                updateScoreMarkers();

                // History & Stats
                let actor = 'Pareja Rival';
                let statActor = 'rivals';
                if (playerIndex === 0) { actor = 'Jugador'; statActor = 'player'; }
                else if (playerIndex === 2) { actor = 'Compañero'; statActor = 'partner'; }

                marker.addHistoryEntry('Aseo', actor, 1, handNumber);
                statsService.registerAction('aseos', statActor);
                if (statActor === 'player') statsService.registerPoints(1, 'player');

                marker.addHistoryEntry(caidaType, actor, caidaPoints, handNumber);
                statsService.registerAction(
                    caidaType === 'Caída' ? 'caidas' : caidaType === 'Contracaída' ? 'contracaidas' : 'sanVicentes',
                    statActor
                );
                if (statActor === 'player') statsService.registerPoints(caidaPoints, 'player');

            } else if (isAseo && isAseoValid) {
                // Just Aseo
                debugLog(`🧹 Aseo! Player ${playerIndex} cleared the board. +1 point for team${aseoTeamId}`);
                if (toastManagerRef.current) {
                    toastManagerRef.current.showPlayerToast(`${TOAST_ICONS.ASEO} Aseo`, playerIndex, {
                        type: 'team' + aseoTeamId,
                        teamColor: aseoTeamColor,
                        duration: 2000
                    });
                }
                await marker.addPoints(1);
                updateScoreMarkers();

                // History & Stats
                let actor = 'Pareja Rival';
                let statActor = 'rivals';
                if (playerIndex === 0) { actor = 'Jugador'; statActor = 'player'; }
                else if (playerIndex === 2) { actor = 'Compañero'; statActor = 'partner'; }

                if (statActor === 'player') statsService.registerPoints(1, 'player');
                marker.addHistoryEntry('Aseo', actor, 1, handNumber);
                statsService.registerAction('aseos', statActor);
            } else if (isAseo && !isAseoValid && caidaType) {
                // Aseo no válido + Caída: solo otorgar puntos de caída
                debugLog(`🧹🎯 Aseo no válido + ${caidaType}. Player ${playerIndex}. Solo se otorgan ${caidaPoints} puntos por ${caidaType}.`);
                if (toastManagerRef.current) {
                    const caidaIcon = caidaType === 'Caída' ? TOAST_ICONS.CAIDA_3 : caidaType === 'Contracaída' ? TOAST_ICONS.CAIDA_4 : '💫';
                    toastManagerRef.current.showPlayerToast(`${caidaIcon} ${caidaType}${SEPARATOR}${TOAST_ICONS.WARNING} Aseo no válido`, playerIndex, {
                        type: 'team' + aseoTeamId,
                        teamColor: aseoTeamColor,
                        duration: 3000
                    });
                }
                // Sound and vibration feedback already triggered before animation
                await marker.addPoints(caidaPoints);
                updateScoreMarkers();

                // History & Stats - solo caída
                let actor = 'Pareja Rival';
                let statActor = 'rivals';
                if (playerIndex === 0) { actor = 'Jugador'; statActor = 'player'; }
                else if (playerIndex === 2) { actor = 'Compañero'; statActor = 'partner'; }

                marker.addHistoryEntry(caidaType, actor, caidaPoints, handNumber);
                statsService.registerAction(
                    caidaType === 'Caída' ? 'caidas' : caidaType === 'Contracaída' ? 'contracaidas' : 'sanVicentes',
                    statActor
                );
                if (statActor === 'player') statsService.registerPoints(caidaPoints, 'player');

            } else if (isAseo && !isAseoValid) {
                // Aseo no válido (última mano, último jugador/repartidor)
                debugLog(`🧹 Aseo no válido - última mano del repartidor. Player ${playerIndex}. No se otorgan puntos.`);
                if (toastManagerRef.current) {
                    toastManagerRef.current.showPlayerToast(`${TOAST_ICONS.WARNING} Aseo no válido${SEPARATOR}${TOAST_ICONS.LAST_HAND} Última mano`, playerIndex, {
                        type: 'info',
                        teamColor: aseoTeamColor,
                        duration: 2000
                    });
                }

            } else if (caidaType) {
                // Just Caída (without Aseo - rare but possible)
                debugLog(`🎯 ${caidaType} on COLLECT! Player ${playerIndex}. +${caidaPoints} points for team${aseoTeamId}`);
                if (toastManagerRef.current) {
                    const caidaIcon = caidaType === 'Caída' ? TOAST_ICONS.CAIDA_3 : caidaType === 'Contracaída' ? TOAST_ICONS.CAIDA_4 : '💫';
                    toastManagerRef.current.showPlayerToast(`${caidaIcon} ${caidaType}`, playerIndex, {
                        type: 'team' + aseoTeamId,
                        teamColor: aseoTeamColor,
                        duration: 2000
                    });
                }
                // Sound and vibration feedback already triggered before animation
                await marker.addPoints(caidaPoints);
                updateScoreMarkers();

                // History & Stats
                let actor = 'Pareja Rival';
                let statActor = 'rivals';
                if (playerIndex === 0) { actor = 'Jugador'; statActor = 'player'; }
                else if (playerIndex === 2) { actor = 'Compañero'; statActor = 'partner'; }

                marker.addHistoryEntry(caidaType, actor, caidaPoints, handNumber);
                statsService.registerAction(
                    caidaType === 'Caída' ? 'caidas' : caidaType === 'Contracaída' ? 'contracaidas' : 'sanVicentes',
                    statActor
                );
                if (statActor === 'player') statsService.registerPoints(caidaPoints, 'player');
            }

            // Note: Win condition is automatically handled by ScoreMarker's onWin callback
            // when addPoints() is called above. No need for manual check here.

            // Reset caída tracking (consecutive drop chain is broken by a collect)
            // FIXED: Using Refs
            lastDroppedValueRef.current = null;
            consecutiveDropCountRef.current = 0;
            setLastDroppedValue(null);
            setConsecutiveDropCount(0);

            // Remove sprites from scene
            cardSprite.destroy();
            matchedCards.forEach(item => item.sprite.destroy());

            // Track collected cards and last collector
            const numCollected = result.collectedCards.length; // Includes played card + matched cards
            const team = playerIndex % 2 === 0 ? 'team1' : 'team2';

            setCollectedCards(prev => ({
                ...prev,
                [team]: prev[team] + numCollected
            }));

            // Add collected cards to DealManager (para guardar las cartas físicas)
            dealManagerRef.current.addCollectedCards(playerIndex, result.collectedCards);

            // Track collected cards for HARD mode AI
            if (aiServiceRef.current) {
                const teamId = (playerIndex === 0 || playerIndex === 2) ? 1 : 2;
                aiServiceRef.current.cardTracker.recordCollection(result.collectedCards, teamId);
            }

            // Update both state AND local variable for synchronous access
            setLastCollector(playerIndex);
            currentCollector = playerIndex;

            debugLog(`Player ${playerIndex} collected ${numCollected} cards. Team totals:`, {
                team1: collectedCards.team1 + (team === 'team1' ? numCollected : 0),
                team2: collectedCards.team2 + (team === 'team2' ? numCollected : 0)
            });
        }

        // 3. Check if all players have empty hands (hand completed)
        const allHandsEmpty = [0, 1, 2, 3].every(i =>
            dealManagerRef.current.getPlayerCards(i).length === 0
        );

        if (allHandsEmpty) {
            // Check if this is the last hand (no more cards in deck)
            const isLastHand = deckRef.current.remaining === 0;
            const isPenultimateHand = deckRef.current.remaining === 12;

            if (isLastHand) {
                // ROUND END: Assign center cards to last collector and count
                debugLog('🏁 Round ended! Processing final cards...');

                const centerCards = dealManagerRef.current.getCenterCards();
                const centerSprites = dealManagerRendererRef.current.getCenterCardSprites();

                // Use currentCollector (sync variable) instead of lastCollector (async state)
                if (centerCards.length > 0 && currentCollector !== null) {
                    // Determine pile position for last collector
                    if (!appRef.current || !appRef.current.screen) {
                        console.warn('[Ronda] Cannot determine final pile position - app or screen is null');
                        return;
                    }
                    const screenWidth = appRef.current.screen.width;
                    const screenHeight = appRef.current.screen.height;
                    const cardWidth = dealManagerRendererRef.current.cardWidth;
                    const cardHeight = dealManagerRendererRef.current.cardHeight;
                    const pileMargin = 80;

                    let pilePos = { x: 0, y: 0 };
                    switch (currentCollector) {
                        case 0:
                            pilePos = { x: screenWidth / 2 + cardWidth * 2, y: screenHeight - pileMargin };
                            break;
                        case 1:
                            pilePos = { x: screenWidth - pileMargin, y: screenHeight / 2 + cardHeight };
                            break;
                        case 2:
                            pilePos = { x: screenWidth / 2 - cardWidth * 2, y: pileMargin };
                            break;
                        case 3:
                            pilePos = { x: pileMargin, y: screenHeight / 2 - cardHeight };
                            break;
                    }

                    // Animate center cards to last collector's pile
                    const centerSpriteArray = Array.from(centerSprites);
                    await dealManagerRendererRef.current.animateCardCollection(centerSpriteArray, pilePos);

                    // Add center cards to DealManager's collected cards
                    const team = currentCollector % 2 === 0 ? 'team1' : 'team2';
                    dealManagerRef.current.addCollectedCards(currentCollector, centerCards);

                    // Remove center sprites
                    centerSpriteArray.forEach(s => s.destroy());

                    debugLog(`Center cards (${centerCards.length}) assigned to Player ${currentCollector}`);
                }

                // Get final counts from DealManager (source of truth)
                const finalCounts = {
                    team1: dealManagerRef.current.collectedCards.team1.length,
                    team2: dealManagerRef.current.collectedCards.team2.length
                };

                setCollectedCards(finalCounts);

                debugLog('Final card counts:', finalCounts);
                debugLog('Recuento finalizado');

                // Calculate and award points
                const team1Points = Math.max(0, finalCounts.team1 - 20);
                const team2Points = Math.max(0, finalCounts.team2 - 20);

                // Register final card collection for teams
                statsService.registerTeamCollection(finalCounts.team1);
                statsService.registerTeamCollection(finalCounts.team2);

                if (team1Points > 0) {
                    await team1Marker.addPoints(team1Points);
                    updateScoreMarkers();
                    debugLog(`Team 1 scored ${team1Points} points (${finalCounts.team1} cards)`);
                }

                if (team2Points > 0) {
                    await team2Marker.addPoints(team2Points);
                    updateScoreMarkers();
                    debugLog(`Team 2 scored ${team2Points} points (${finalCounts.team2} cards)`);
                }

                // Show hand summary modal instead of toast
                setHandSummaryData({
                    team1Cards: finalCounts.team1,
                    team2Cards: finalCounts.team2,
                    team1Points: team1Points,
                    team2Points: team2Points
                });
                setShowHandSummaryModal(true);

                // Wait for user to close the modal
                // The modal's onContinue will call handleHandSummaryContinue
                setIsProcessingTurn(false);
                return;
            } else {
                // Continue with next hand
                debugLog('🎴 Hand completed! Re-dealing cards...');

                // Mark as dealing for header display
                setIsDealing(true);

                await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.dealing.beforeNewHand));

                // Verificar que startingDealer existe
                let dealerPlayerIndex = 0;
                if (!startingDealer) {
                    console.error('❌ startingDealer is null, cannot deal cards');
                    console.error('   Players:', players.map(p => ({ id: p.id, name: p.name })));
                    console.error('   Attempting recovery from lastCollector...');

                    // Intentar recuperar el dealer desde el lastCollector o defaultear al jugador 0
                    if (lastCollector !== null && lastCollector !== undefined) {
                        const recoveredDealer = players[lastCollector];
                        console.warn('   Recovered dealer from lastCollector:', recoveredDealer);
                        setStartingDealer(recoveredDealer);
                        dealerPlayerIndex = lastCollector;
                    } else {
                        // Si no hay lastCollector, usar jugador 0
                        console.warn('   Using player 0 as fallback dealer');
                        setStartingDealer(players[0]);
                        dealerPlayerIndex = 0;
                    }
                } else {
                    dealerPlayerIndex = players.findIndex(p => p.id === startingDealer.id);
                }

                await dealManagerRef.current.dealHand(dealerPlayerIndex, false);
                await dealManagerRendererRef.current.animateDealSubsequentHand(dealerPlayerIndex);

                debugLog('Cards re-dealt');

                // Mark dealing as complete
                setIsDealing(false);

                // Reset caída tracking after dealing new cards
                // A caída cannot occur on the first play after dealing
                lastDroppedValueRef.current = null;
                consecutiveDropCountRef.current = 0;
                setLastDroppedValue(null);
                setConsecutiveDropCount(0);

                if (isPenultimateHand) {
                    if (toastManagerRef.current) {
                        toastManagerRef.current.showToast(`${TOAST_ICONS.SE_HAN_IDO} Se han ido${SEPARATOR}${TOAST_ICONS.LAST_HAND} Última mano`, {
                            type: 'info',
                            duration: 3000,
                            position: 'center'
                        });
                    }
                }

                // Detect Rondas/Rondines in new hand
                const firstTurnIndex = (dealerPlayerIndex + 1) % 4;
                // IMPORTANT: Reset currentTurn to null before detection to prevent useEffect from triggering AI turns prematurely
                setCurrentTurn(null);
                setIsProcessingTurn(false);
                await handleRondaDetection(firstTurnIndex);
                return; // handleRondaDetection will set the turn
            }
        }

        // 4. Check if round was won before advancing turn
        // (The onWin callback may have been triggered by addPoints above)
        if (team1Marker.score >= 18 || team2Marker.score >= 18) {
            debugLog('🏆 Round won! Not advancing turn.');
            setIsProcessingTurn(false);
            return;
        }

        // 5. Advance turn
        const nextTurnIndex = rondaLogicRef.current.getNextTurn(playerIndex);
        setCurrentTurn(nextTurnIndex);
        setIsProcessingTurn(false);
    };

    // Helper function to update score markers
    const updateScoreMarkers = () => {
        if (rendererRef.current) {
            rendererRef.current.update();
        }
    };

    // Start a new round with next dealer
    const startNewRound = async (dealerToUse = null) => {
        debugLog('🔄 Starting new round...');

        // Use provided dealer or calculate from current state
        let nextDealer;
        let nextDealerIndex;

        if (dealerToUse) {
            // Dealer was provided (called from handleWin)
            nextDealer = dealerToUse;
            nextDealerIndex = players.findIndex(p => p.id === nextDealer.id);
            debugLog('🎲 Using provided dealer:', { id: nextDealer.id, name: nextDealer.name, calculatedIndex: nextDealerIndex });
        } else {
            // Calculate from current state (called from elsewhere)
            if (!startingDealer) {
                console.error('❌ startingDealer is null, cannot start new round');
                return;
            }
            const currentDealerIndex = players.findIndex(p => p.id === startingDealer.id);
            nextDealerIndex = (currentDealerIndex + 1) % 4;
            nextDealer = players[nextDealerIndex];
            debugLog(`🎲 New dealer: Player ${nextDealerIndex + 1} (${nextDealer.name})`);
        }

        // Disable turn processing during reset
        setCurrentTurn(null);
        setIsProcessingTurn(true);

        // Update dealer state
        setStartingDealer(nextDealer);

        // Reset game tracking states (but NOT score markers)
        setLastCollector(null);
        setCollectedCards({ team1: 0, team2: 0 });
        setLastDroppedValue(null);
        setConsecutiveDropCount(0);

        // Reset ronda state to clear any previous declarations from last hand
        setRondaState({
            phase: 'none',
            declarations: [],
            validDeclarations: [],
            needsEnvio: false,
            envioParticipants: [],
            currentEnvioPlayer: null,
            envioHistory: [],
            pendingEnvio: null,
            resolution: null,
            decliningTeam: null,
            player1Declaration: null,
            showButtons: false,
            availableDecisions: [],
            manoPlayerIndex: null,
            revealedMedias: {},
            envioAccepted: false,
            announcedPlayers: [],
            sortedDeclarations: [],
        });

        // Reset collected cards in DealManager
        if (dealManagerRef.current) {
            dealManagerRef.current.resetCollectedCards();
        }

        // Clear all card sprites first
        if (dealManagerRendererRef.current) {
            dealManagerRendererRef.current.clearAllCards();
        }

        // Destroy old deck renderer
        if (deckRendererRef.current) {
            deckRendererRef.current.destroy();
        }

        // Create new deck
        const newDeck = new Deck();
        newDeck.shuffle();
        deckRef.current = newDeck;

        // Reset card tracker for new round
        if (aiServiceRef.current) aiServiceRef.current.resetCardTracker();

        // Update DealManager with new deck (also resets isFirstDeal and clears cards)
        dealManagerRef.current.setDeck(newDeck);

        // Create new deck renderer with new deck
        const DeckRenderer = (await import('../game/DeckRenderer')).default;
        const newDeckRenderer = new DeckRenderer(appRef.current, newDeck);
        newDeckRenderer.setCardSize(dealManagerRendererRef.current.cardWidth, dealManagerRendererRef.current.cardHeight);
        if (appRef.current && appRef.current.screen) {
            newDeckRenderer.setPositionByPlayer(nextDealerIndex, appRef.current.screen.width, appRef.current.screen.height);
        }
        await newDeckRenderer.render();
        deckRendererRef.current = newDeckRenderer;

        // Update DealManagerRenderer with new deck renderer
        if (dealManagerRendererRef.current) {
            dealManagerRendererRef.current.setDeckRenderer(newDeckRenderer);
        }

        // Show shuffling modal (dealer changed, not first deal)
        setShufflingPlayerName(nextDealer.name);
        setShufflingPlayerAvatar(nextDealer.avatar);
        setShowShufflingModal(true);

        // Mark as dealing for header display
        setIsDealing(true);

        // Wait for shuffling modal duration
        await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.dealing.shufflingModal));

        // Hide shuffling modal
        setShowShufflingModal(false);

        // Deal new hand (true = deal to center like first hand, but without cutting sequence)
        await dealManagerRef.current.dealHand(nextDealerIndex, true);
        await dealManagerRendererRef.current.animateDealFirstHand(nextDealerIndex);

        // Track initial center cards for HARD mode AI
        if (aiServiceRef.current && dealManagerRef.current) {
            aiServiceRef.current.cardTracker.setCenterCards(dealManagerRef.current.getCenterCards());
        }

        // Mark dealing as complete
        setIsDealing(false);

        // Detect Rondas/Rondines in new hand
        const firstTurnIndex = (nextDealerIndex + 1) % 4;
        setIsProcessingTurn(false);
        await handleRondaDetection(firstTurnIndex);
    };

    // Expose debug functions to window for console access
    useEffect(() => {
        // Only expose debug functions if in debug mode
        if (isDebugMode()) {
            window.addPointsTeam1 = (points) => {
                team1Marker.addPoints(points);
                updateScoreMarkers();
                debugLog(`✅ Added ${points} points to Team 1. New score: ${team1Marker.score}`);
            };

            window.subtractPointsTeam1 = (points) => {
                team1Marker.subtractPoints(points);
                updateScoreMarkers();
                debugLog(`➖ Subtracted ${points} points from Team 1. New score: ${team1Marker.score}`);
            };

            window.addPointsTeam2 = (points) => {
                team2Marker.addPoints(points);
                updateScoreMarkers();
                debugLog(`✅ Added ${points} points to Team 2. New score: ${team2Marker.score}`);
            };

            window.subtractPointsTeam2 = (points) => {
                team2Marker.subtractPoints(points);
                updateScoreMarkers();
                debugLog(`➖ Subtracted ${points} points from Team 2. New score: ${team2Marker.score}`);
            };
        }

        window.getGameState = () => {
            const state = {
                team1: {
                    score: team1Marker.score,
                    status: team1Marker.isInMalas() ? 'malas' : 'buenas'
                },
                team2: {
                    score: team2Marker.score,
                    status: team2Marker.isInMalas() ? 'malas' : 'buenas'
                },
                startingDealer: startingDealer ? {
                    name: startingDealer.name,
                    avatar: startingDealer.avatar
                } : null,
                currentTurn: currentTurn !== null ? {
                    index: currentTurn,
                    name: players[currentTurn]?.name
                } : null
            };
            debugTable(state);
            return state;
        };

        // Log initial state
        debugLog('🎮 Ronda Game Initialized');
        debugLog('📊 Team 1:', team1Marker.score, 'pts', `(${team1Marker.isInMalas() ? 'malas' : 'buenas'})`);
        debugLog('📊 Team 2:', team2Marker.score, 'pts', `(${team2Marker.isInMalas() ? 'malas' : 'buenas'})`);
        debugLog('💡 Debug functions available:');
        debugLog('  - addPointsTeam1(points)');
        debugLog('  - subtractPointsTeam1(points)');
        debugLog('  - addPointsTeam2(points)');
        debugLog('  - subtractPointsTeam2(points)');
        debugLog('  - getGameState()');

        // Cleanup
        return () => {
            delete window.addPointsTeam1;
            delete window.subtractPointsTeam1;
            delete window.addPointsTeam2;
            delete window.subtractPointsTeam2;
            delete window.getGameState;
        };
    }, [team1Marker, team2Marker, startingDealer]);

    // Table color state
    const [tableColor, setTableColor] = useState(() => {
        const savedColor = localStorage.getItem('tableColor');
        return savedColor ? parseInt(savedColor.replace('#', '0x'), 16) : 0x257241;
    });

    // Listen for table color changes
    useEffect(() => {
        const handleColorChange = (e) => {
            const newColor = parseInt(e.detail.replace('#', '0x'), 16);
            setTableColor(newColor);
        };

        window.addEventListener('tableColorChanged', handleColorChange);
        return () => window.removeEventListener('tableColorChanged', handleColorChange);
    }, []);

    // CSS styles for rotation fallback when Screen Orientation API fails
    const rotationWrapperStyle = needsRotation ? {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vh',
        height: '100vw',
        transform: 'rotate(90deg)',
        transformOrigin: 'top left',
        marginLeft: '100vw',
        overflow: 'hidden',
    } : {};

    return (
        <Box sx={rotationWrapperStyle}>
            {/* Show GameStartSequence only at the beginning when both teams have 0 points */}
            {shouldShowGameStart && (
                <GameStartSequence
                    players={players}
                    onComplete={handleGameStartComplete}
                />
            )}

            {/* Main game interface */}
            {!shouldShowGameStart && (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: needsRotation ? '100vw' : '100dvh',
                    width: needsRotation ? '100vh' : '100%',
                    overflow: 'hidden',
                    padding: 0,
                    margin: 0,
                    alignItems: 'stretch'
                }}>
                    <Box ref={headerRef} sx={{ width: '100%', flexShrink: 0 }}>
                        <Header
                            title={`Ronda`}
                            showSettings={false}
                            dealer={startingDealer ? {
                                name: startingDealer.name,
                                avatar: startingDealer.avatar
                            } : null}
                            player={currentTurn !== null && players[currentTurn] ? {
                                name: players[currentTurn].name,
                                avatar: players[currentTurn].avatar
                            } : null}
                            onBack={() => navigate('/')}
                            globalScore={globalScore}
                            players={players}
                            isDealing={isDealing}
                            onInGameSettings={() => setInGameSettingsOpen(true)}
                        />
                    </Box>

                    <Box
                        sx={{
                            overflow: 'hidden',
                            padding: 0,
                            margin: 0,
                            minHeight: 0,
                            // If we have measured the header height, set the GameTable height
                            // to the remaining viewport space: calc(100dvh - headerHeight)
                            // Use 100vw for height when rotated via CSS
                            height: headerHeight 
                                ? (needsRotation ? `calc(100vw - ${headerHeight}px)` : `calc(100dvh - ${headerHeight}px)`)
                                : 'auto',
                        }}
                    >
                        <GameTable onMount={setupGame} backgroundColor={tableColor} />
                    </Box>
                </Box>
            )}
            {/* Game Winner Modal */}
            <GameWinnerModal
                open={showWinnerModal}
                winnerTeamId={winnerTeamId}
                players={players}
                gamePoints={gamePoints}
                onHome={async () => {
                    await gameStateManager.clearSavedGame();
                    navigate('/');
                }}
            />

            {/* Round Winner Modal - for round wins, not match wins */}
            <RoundWinnerModal
                open={showRoundWinnerModal}
                winnerTeamId={roundWinnerTeamId}
                gameName="Ronda"
                nextGameName={pendingNextGame?.nextGameName}
                players={players}
                teamColors={{ team1: TEAM_COLORS.team1.main, team2: TEAM_COLORS.team2.main }}
                onContinue={handleRoundWinnerContinue}
            />

            {/* Hand Summary Modal - shown after card counting */}
            <RondaHandSummaryModal
                open={showHandSummaryModal}
                summaryData={handSummaryData}
                players={players}
                onContinue={handleHandSummaryContinue}
            />

            {/* Score History Modal */}
            <ScoreHistoryModal
                open={historyModalOpen}
                onClose={() => setHistoryModalOpen(false)}
                history={selectedTeamHistory}
                teamId={selectedTeamId}
            />

            {/* Ronda/Rondín Decision Buttons */}
            <RondaDecisionButtons
                visible={rondaState.showButtons}
                decisions={rondaState.availableDecisions}
                declaration={rondaState.player1Declaration}
                onDecision={handlePlayer1RondaDecision}
                disabled={isProcessingTurn}
                playerName={players[0]?.name || 'Jugador 1'}
            />

            {/* Caída Opportunity Buttons */}
            <CaidaOpportunityButtons
                visible={caidaOpportunity.active}
                caidaType={caidaOpportunity.caidaType}
                cardValue={caidaOpportunity.cardValue}
                onDecision={handleCaidaDecision}
                disabled={isProcessingTurn}
                playerName={players[0]?.name || 'Jugador 1'}
            />

            {/* In-Game Settings Modal */}
            <GameSettingsInGameModal
                open={inGameSettingsOpen}
                onClose={() => setInGameSettingsOpen(false)}
                isPaused={isPaused}
                onPauseToggle={handlePauseToggle}
                gameType="ronda"
                getGameState={getGameStateForSave}
                currentPhase="playing"
            />

            {/* Shuffling Modal - shown when dealer changes */}
            <ShufflingModal
                open={showShufflingModal}
                playerName={shufflingPlayerName}
                playerAvatar={shufflingPlayerAvatar}
            />
        </Box>
    );
};

export default Ronda;
